// File: src/pages/Invoices/InvoiceForm.jsx
import React, { useState, useEffect, useContext, useRef, useMemo, useDeferredValue } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Autocomplete,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import axios from "../../axios";
import invoiceApi from "../../api/invoiceApi";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Card, CardContent } from "../../components/ui/card";
import { SettingsContext } from "../../contexts/SettingsContext";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import CustomerForm from "../customerSuplier/CustomerForm";
import SupplierForm from "../customerSuplier/SupplierForm";
import ImportInvoiceItemsDialog from "./ImportInvoiceItemsDialog";
import InvoiceLayoutDialog, {
  DEFAULT_HEADER_FIELDS, DEFAULT_ITEM_FIELDS, DEFAULT_ITEM_ORDER, DEFAULT_ITEM_WIDTHS, LOCKED_ITEM_FIELDS, HEADER_ALIASES,
  buildLayoutXml, parseLayoutXml, buildItemsLayoutXml, parseItemsLayoutXml,
} from "./InvoiceLayoutDialog";
import AddIcon from '@mui/icons-material/Add';
import * as XLSX from "xlsx";

const initialFormState = {
  invoiceTypeId: 0,
  invoiceNumber: "",
  paidAmount: 0,
  issueDate: new Date().toISOString(),
  referenceDate: new Date().toISOString(),
  referenceNumber: "-1",
  customerId: 0,
  supplierId: 0,
  customerPrevBalance: 0,
  stockId: 0,
  toStockId: 0,
  salesmanId: null,
  paymentMethodId: 0,
  safeId: 0,
  bankId: 0,
  notes: "",
  discount: 0,
  invoiceStatus: 0,
  lines: [],
  journalEntryGlobalId: null, // ✅ Add this
  customHeaders: {},          // الحقول المخصصة لنوع الفاتورة: { FieldName: "القيمة" }
};

// ───────────── بحث الأصناف (محلي بالكامل) ─────────────
// توحيد النص العربي: بدون تشكيل، أ/إ/آ ← ا، ة ← ه، ى ← ي، والأرقام العربية ← إنجليزية
const AR_DIACRITICS = /[\u064B-\u0652\u0670\u0640]/g;
// سطر لم يُختر له صنف ولم يُكتب فيه شيء
const isBlankLine = (ln) => !ln?.productId && !String(ln?.productName ?? "").trim();

function normalizeSearch(v) {
  return String(v ?? "")
    .toLowerCase()
    .replace(AR_DIACRITICS, "")
    .replace(/[\u0623\u0625\u0622]/g, "\u0627")
    .replace(/\u0629/g, "\u0647")
    .replace(/\u0649/g, "\u064A")
    .replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06F0-\u06F9]/g, d => String(d.charCodeAt(0) - 0x06F0))
    .replace(/\s+/g, " ")
    .trim();
}

export default function InvoiceForm({ viewMode = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { id, typeId } = useParams();
  const [searchParams] = useSearchParams();
  const returnOf = searchParams.get("returnOf");
  const returnTypeId = searchParams.get("returnTypeId");
  const fromQuoteId = searchParams.get("fromQuote") || searchParams.get("Qtation");
  const copyOfId = searchParams.get("copyOf"); // نسخ فاتورة: فاتورة جديدة بنفس بيانات السابقة
  const copyLoadedRef = useRef(false);
  // عميل الفاتورة المحمّلة من الخادم (null = فاتورة جديدة) — رصيده السابق محفوظ فيها
  const loadedCustomerRef = useRef(null);
  const [copySourceNumber, setCopySourceNumber] = useState(null);

  const [editingId, setEditingId] = useState(id ?? null);
  const mode = viewMode
    ? "view"
    : editingId
    ? "edit"
    : returnOf
    ? "return"
    : fromQuoteId
    ? "fromQuote"
    : copyOfId
    ? "copy"
    : "new";

  // المرتجع: النوع من returnTypeId ثم من الرابط /invoices/new/4 ثم مرتجع مبيعات افتراضيًا
  const initialType = returnOf
    ? (returnTypeId ? parseInt(returnTypeId, 10) : typeId ? parseInt(typeId, 10) : 3)
    : fromQuoteId
    ? 1
    : typeId
    ? parseInt(typeId, 10)
    : 0;

  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const branchId = parseInt(localStorage.getItem("branchId"), 10);
  const userId = parseInt(localStorage.getItem("userId"), 10);
  
  // Security Context
  const restrictedSafeId = localStorage.getItem("safeId") ? parseInt(localStorage.getItem("safeId"), 10) : null;
  const restrictedStockId = localStorage.getItem("stockId") ? parseInt(localStorage.getItem("stockId"), 10) : null;
  
  // Multi-context support
  const allowedSafeIds = localStorage.getItem("allowedSafeIds") ? JSON.parse(localStorage.getItem("allowedSafeIds")) : [];
  const allowedStockIds = localStorage.getItem("allowedStockIds") ? JSON.parse(localStorage.getItem("allowedStockIds")) : [];

  const getLabel = (fieldName) => {
    return t(`invoice.fields.${fieldName}`);
  };

  // ── تخصيص المظهر (لكل نوع فاتورة في الشركة): حقول الرأس القابلة للإخفاء + أعمدة السطور
  const [visibleFields, setVisibleFields] = useState({ ...DEFAULT_HEADER_FIELDS });
  const [visibleItemFields, setVisibleItemFields] = useState({ ...DEFAULT_ITEM_FIELDS });
  const [itemOrder, setItemOrder] = useState([...DEFAULT_ITEM_ORDER]);        // ترتيب أعمدة السطور
  const [itemWidths, setItemWidths] = useState({ ...DEFAULT_ITEM_WIDTHS });   // عرض كل عمود (null = تلقائي)
  const [layoutOpen, setLayoutOpen] = useState(false);

  const headerLabels = {
    Notes: t('invoice.fields.notes'),
    InvoiceStatus: t('invoice.fields.invoiceStatus'),
    Reference: `${t('invoice.fields.referenceNumber')} / ${t('invoice.fields.referenceDate')}`,
    CustomerPrevBalance: t('invoice.fields.customerPrevBalance'),
  };
  const itemLabels = {
    ProductId: t('invoice.fields.item'),
    Description: t('invoice.fields.description'),
    Unit: t('invoice.fields.unit'),
    Quantity: t('invoice.fields.quantity'),
    UnitPrice: t('invoice.fields.price'),
    LineTotal: t('invoice.fields.total'),
    Discount: t('invoice.fields.discount'),
    TaxPercent: t('invoice.fields.taxPercent'),
    Net: t('invoice.fields.net'),
  };
  // الأعمدة الظاهرة بالترتيب المحفوظ
  const visibleColumns = itemOrder.filter(k => visibleItemFields[k]);

  const focusLineField = (rowIndex, field) => {
    if (rowIndex == null || !field) return false;
    const element = document.querySelector(`[data-line-field="${field}-${rowIndex}"]`);
    if (!element || element.disabled) return false;
    element.focus();
    if (typeof element.select === "function") {
      element.select();
    }
    return true;
  };

  const getFirstLineField = () => {
    if (visibleItemFields.ProductId) return "productName";
    if (visibleItemFields.Description) return "description";
    if (visibleItemFields.Unit) return "unitId";
    if (visibleItemFields.Quantity) return "qty";
    if (visibleItemFields.UnitPrice) return "price";
    if (visibleItemFields.Discount) return "discount";
    return null;
  };

  const { decimalPlaces } = useContext(SettingsContext);
  const stepValue = 1 / 10 ** decimalPlaces;

  const [types, setTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [methods, setMethods] = useState([]);
  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [creditMethodId, setCreditMethodId] = useState(null);

  const [priceIncludesVAT, setPriceIncludesVAT] = useState(false);

  const [settings, setSettings] = useState({
    isPriceIncludesVAT: false,
    autoApproveInvoices: false,
    autoPrintAfterSave: false,
    allowNegativeSales: false,
    pricingPolicy: "",
    decimalPlaces: 2,
    isVatExempt: false,
  });

  const [form, setForm] = useState({
    ...initialFormState,
    invoiceTypeId: initialType,
  });

  const [productSearch, setProductSearch] = useState("");
  const [showApproveButton, setShowApproveButton] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canUnapprove, setCanUnapprove] = useState(false);
  const [isZatcaLocked, setIsZatcaLocked] = useState(false);
  const [zatcaStatus, setZatcaStatus] = useState(null); // REPORTED, FAILED, CLEARED
  const [zatcaResponse, setZatcaResponse] = useState(null); // For details if needed
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });
  
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [openSupplierModal, setOpenSupplierModal] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleImportSuccess = (importedItems) => {
    const newLines = importedItems.map(item => {
        const { product: p, qty, price } = item;
        const unitsList = Array.isArray(p.units) ? p.units : [];
        const bu = unitsList.find(u => u.isBaseUnit) || {};
        
        // Use imported price if available, otherwise calculate based on policy
        // Note: pickUnitPriceForDirection uses current form state (isInbound)
        const unitPrice = price !== null ? price : pickUnitPriceForDirection(bu, p);
        
        return {
            productId: p.id,
            productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
            description: p?.description ?? "",
            unitId: bu?.unitId || 0,
            unitName: bu?.unitNameAr || "",
            qty: parseFloat(qty).toFixed(decimalPlaces),
            price: parseFloat(unitPrice).toFixed(decimalPlaces),
            discount: parseFloat(0).toFixed(decimalPlaces),
            taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
            unitsList,
        };
    });

    setForm(f => ({ ...f, lines: [...f.lines.filter(ln => !isBlankLine(ln)), ...newLines] }));
  };

  const handleCustomerSuccess = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer]);
    setForm(f => ({ ...f, customerId: newCustomer.id }));
    setOpenCustomerModal(false);
    toast.success("تم إضافة العميل بنجاح وتحديده");
  };

  const handleSupplierSuccess = (newSupplier) => {
    setSuppliers(prev => [...prev, newSupplier]);
    setForm(f => ({ ...f, supplierId: newSupplier.id }));
    setOpenSupplierModal(false);
    toast.success("تم إضافة المورد بنجاح وتحديده");
  };
const togglePriceIncludesVAT = () => {
  setPriceIncludesVAT(prev => !prev);
};

  // خصم الفاتورة بالنسبة (null = الخصم مُدخل بالمبلغ)
  const [docDiscountPct, setDocDiscountPct] = useState(null);

  // إدراج متعدد للأصناف
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSearch, setBulkSearch] = useState("");
  // خريطة: productId -> كمية
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkBalances, setBulkBalances] = useState({});      // productId -> الرصيد بالوحدة الأساسية
  const [bulkBalancesLoading, setBulkBalancesLoading] = useState(false);
  const [bulkOnlySelected, setBulkOnlySelected] = useState(false);
  const deferredBulkSearch = useDeferredValue(bulkSearch);  // الكتابة تبقى سريعة حتى مع آلاف الأصناف

  // ── الصنف الخدمي ليس له مخزون: لا يظهر في الحركات المخزنية فقط
  //    (رصيد افتتاحي، إدخال، إخراج، مناقلة، تسوية بالعجز/بالزيادة)
  const STOCK_ONLY_TYPES = [7, 8, 9, 12, 13, 99];
  const isStockOnlyType = STOCK_ONLY_TYPES.includes(Number(form.invoiceTypeId));
  const pickableProducts = useMemo(
    () => (isStockOnlyType ? products.filter(p => !p.isService) : products),
    [products, isStockOnlyType]
  );
  const isServiceProduct = (productId) => !!products.find(p => p.id === productId)?.isService;

  // فهرس البحث: يُبنى مرة واحدة عند تحميل الأصناف (لا طلبات للخادم أثناء البحث)
  const productSearchIndex = useMemo(() => pickableProducts.map(p => {
    const units = Array.isArray(p.units) ? p.units : [];
    const text = normalizeSearch([
      p.arabicName, p.englishName, p.code, p.itemId, p.barcode, p.categoryName,
      ...units.map(u => u.barcode),
    ].filter(Boolean).join(" "));
    return { p, text };
  }), [pickableProducts]);

  // كل كلمة في البحث يجب أن تظهر (بأي ترتيب): "زيت 1.5" تجد "زيت عباد الشمس 1.5 لتر"
  const BULK_LIMIT = 200;
  const bulkResults = useMemo(() => {
    const words = normalizeSearch(deferredBulkSearch).split(" ").filter(Boolean);
    let list = productSearchIndex;
    if (bulkOnlySelected) list = list.filter(x => Object.prototype.hasOwnProperty.call(bulkSelected, x.p.id));
    if (words.length) {
      list = list.filter(x => words.every(w => x.text.includes(w)));
      // المطابقة التامة للباركود/الكود أولًا ثم ما يبدأ بالبحث
      const q = words.join(" ");
      const rank = (x) => {
        const p = x.p;
        if (String(p.barcode || "") === q || String(p.code || "").toLowerCase() === q) return 0;
        if (normalizeSearch(p.arabicName).startsWith(q)) return 1;
        return 2;
      };
      list = [...list].sort((a, b) => rank(a) - rank(b));
    }
    return { total: list.length, rows: list.slice(0, BULK_LIMIT).map(x => x.p) };
  }, [productSearchIndex, deferredBulkSearch, bulkOnlySelected, bulkSelected]);

  // قراءة الباركود وإدراج الصنف تلقائياً
  const [barcode, setBarcode] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const pendingLineFocusRef = useRef(null);

  async function insertProductByBarcode(code) {
    const raw = (code || "").trim();
    if (!raw) return;
    try {
      setBarcodeLoading(true);
      // ابحث أولاً على مستوى المنتج
      let p = pickableProducts.find(pr => (pr.barcode || "").toString() === raw);
      let chosenUnit = null;
      if (!p) {
        // ابحث على مستوى الوحدات لكل منتج
        for (const pr of pickableProducts) {
          const units = Array.isArray(pr.units) ? pr.units : [];
          const match = units.find(u => (u.barcode || "").toString() === raw);
          if (match) {
            p = pr;
            chosenUnit = match;
            break;
          }
        }
      }

      if (!p) {
        toast.error("❌ لم يتم العثور على صنف بهذا الباركود");
        return;
      }

      // التحقق من الرصيد عند المبيعات الصادرة (الخدمة بلا مخزون)
      if (isOutbound && !p.isService) {
        try {
          const response = await axios.get(
            `/StockMovements/stock-balance/${companyId}`,
            { params: { productId: p.id, branchId, stockId: form.stockId } }
          );
          const availableQty = parseFloat(response.data ?? 0);
          if (!settings.allowNegativeSales && availableQty <= 0) {
            toast.error(t('invoice.messages.quantityNotAvailable'));
            return;
          }
        } catch {
          if (!settings.allowNegativeSales) {
            toast.warning(t('invoice.messages.cannotVerifyQuantity'));
            return;
          }
        }
      }

      const unitsList = Array.isArray(p.units) ? p.units : [];
      const baseUnit = chosenUnit || unitsList.find(u => u.isBaseUnit) || {};
      const line = {
        productId: p.id,
        productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
        description: p?.description ?? "",
        unitId: baseUnit?.unitId || 0,
        unitName: baseUnit?.unitNameAr || "",
        qty: parseFloat(1).toFixed(decimalPlaces),
        price: pickUnitPriceForDirection(baseUnit, p),
        discount: parseFloat(0).toFixed(decimalPlaces),
        taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
        unitsList,
      };

      setForm(f => ({ ...f, lines: [...f.lines.filter(ln => !isBlankLine(ln)), line] }));
      toast.success("✅ تم إدراج الصنف عبر الباركود");
    } finally {
      setBarcode("");
      setBarcodeLoading(false);
    }
  }

  function handleBarcodeKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      insertProductByBarcode(barcode);
    }
  }

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/InvoiceTypes?companyId=${companyId}`).then(r => setTypes(r.data));
    axios.get(`/Customer/by-company/${companyId}`).then(r => setCustomers(r.data));
    axios.get(`/Supplier/by-company/${companyId}`).then(r => setSuppliers(r.data));
    axios.get(`/PaymentMethod/by-company/${companyId}`).then(r => setMethods(r.data));
    axios.get(`/Safe/by-company/${companyId}`).then(r => setSafes(r.data));
    axios.get(`/Bank/by-company/${companyId}`).then(r => setBanks(r.data));
    axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data));
    axios.get(`/Product/by-company/${companyId}`)
      .then(r => setProducts(Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.data || [])))
      .catch(console.error)
      .finally(() => setProductsLoaded(true));
  }, [companyId]);

  // تحميل المظهر المحفوظ لنوع الفاتورة — بدونه: الافتراضي (لا يبقى مظهر النوع السابق)
  useEffect(() => {
    if (!companyId || !form.invoiceTypeId) return;
    let alive = true;
    setVisibleFields({ ...DEFAULT_HEADER_FIELDS });
    setVisibleItemFields({ ...DEFAULT_ITEM_FIELDS });
    setItemOrder([...DEFAULT_ITEM_ORDER]);
    setItemWidths({ ...DEFAULT_ITEM_WIDTHS });
    axios
      .get(`/InvoiceLayout/${companyId}/${form.invoiceTypeId}`)
      .then((res) => {
        if (!alive || !res.data) return;
        setVisibleFields(parseLayoutXml(res.data.headerLayoutXml, DEFAULT_HEADER_FIELDS, { aliases: HEADER_ALIASES }));
        const cols = parseItemsLayoutXml(res.data.itemsLayoutXml);
        setVisibleItemFields(cols.visible);
        setItemOrder(cols.order);
        setItemWidths(cols.widths);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [companyId, form.invoiceTypeId]);

  const handleSaveLayout = async (headerFields, itemFields, order = itemOrder, widths = itemWidths) => {
    const items = { ...itemFields };
    LOCKED_ITEM_FIELDS.forEach((k) => { items[k] = true; });
    try {
      await axios.post("/InvoiceLayout/save", {
        companyId,
        invoiceTypeId: form.invoiceTypeId,
        headerLayoutXml: buildLayoutXml(headerFields),
        itemsLayoutXml: buildItemsLayoutXml(items, order, widths),
        userName: localStorage.getItem("username") || "system",
      });
      setVisibleFields(headerFields);
      setVisibleItemFields(items);
      setItemOrder(order);
      setItemWidths(widths);
      toast.success(t('invoice.messages.appearanceSaved'));
      return true;
    } catch {
      toast.error(t('invoice.messages.appearanceSaveFailed'));
      return false;
    }
  };

  const partyForType = (typeId) => {
    if ([1, 9, 3, 10, 5].includes(typeId)) return "customer";
    if ([2, 8, 7, 4, 6].includes(typeId)) return "supplier";
    // 12 = مناقلة, 13 = تسوية بالعجز, 99 = تسوية بالزيادة -> حركة داخلية بلا عميل ولا مورد
    return null;
  };

  const currentType = types.find(t => t.invoiceTypeId === form.invoiceTypeId);
  const direction = currentType?.direction ?? currentType?.Direction;
  const isInbound = direction === 1;
  const isStockAdjustment = form.invoiceTypeId === 13 || form.invoiceTypeId === 99;
  const isTransfer = form.invoiceTypeId === 12;
  const isOutbound = direction === 2;

  const party = partyForType(form.invoiceTypeId);
  const isCustomerParty = party === "customer";
  const isSupplierParty = party === "supplier";

  useEffect(() => {
    const credit = methods.find(m => m.nameAr === "آجل")?.id ?? null;
    setCreditMethodId(credit);
  }, [methods]);

  useEffect(() => {
    const party = partyForType(form.invoiceTypeId);
    setForm(f => ({
      ...f,
      customerId: party === "customer" ? (f.customerId || 0) : 0,
      supplierId: party === "supplier" ? (f.supplierId || 0) : 0,
    }));
  }, [form.invoiceTypeId]);

  const fetchNextInvoiceNumber = async typeId => {
    if (!typeId || (mode !== "new" && mode !== "fromQuote" && mode !== "copy")) return;
    try {
      const res = await invoiceApi.getNextNumber(companyId, branchId, typeId);
      // ✅ التوافق: إذا كانت الاستجابة object بها displayNumber نستخدمه، وإلا نستخدم القيمة القديمة (int)
      const displayNumber =
        (res.data && typeof res.data === "object" && res.data.displayNumber)
          ? res.data.displayNumber
          : res.data?.toString();
      setForm(f => ({
        ...f,
        invoiceNumber: displayNumber,
        issueDate: new Date().toLocaleDateString('en-CA'),
        referenceDate: new Date().toLocaleDateString('en-CA'),
      }));
    } catch (error) {
      console.error(t('invoice.messages.fetchNextNumberFailed'), error);
    }
  };

  useEffect(() => {
    fetchNextInvoiceNumber(form.invoiceTypeId);
  }, [form.invoiceTypeId]);

  useEffect(() => {
    if (!companyId || !form.invoiceTypeId || methods.length === 0) return;

    axios.get("/ProgramSettings/by-company-and-type", {
      params: { companyId, invoiceTypeId: form.invoiceTypeId },
    })
      .then(r => {
        const s = r.data;
        setSettings(s);

        setPriceIncludesVAT(mode === "new" ? !!s.isPriceIncludesVAT : false);

        if (mode === "new") {
          const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
          
          // Determine Safe/Stock IDs: Priority to Restricted > Settings > Default
          // ✅ f كانت تُقرأ خارج setForm (ReferenceError إذا لم يوجد صندوق/مستودع افتراضي)
          setForm(f => {
            const finalSafeId = restrictedSafeId || s.defaultSafeId || f.safeId;
            const finalStockId = restrictedStockId || s.defaultStockId || f.stockId;
            return {
            ...f,
            customerId: s.defaultCustomerId ?? f.customerId,
            paymentMethodId: s.defaultPaymentMethodId ?? f.paymentMethodId,
            safeId: (s.defaultPaymentMethodId ?? f.paymentMethodId) === cashId ? finalSafeId : 0,
            bankId: (s.defaultPaymentMethodId ?? f.paymentMethodId) !== cashId ? (s.defaultBankId ?? f.bankId) : 0,
            stockId: finalStockId,
            invoiceStatus: s.autoApproveInvoices ? 1 : 0,
          };
          });
        }
      })
      .catch(console.error);
  }, [companyId, form.invoiceTypeId, methods, mode, restrictedSafeId, restrictedStockId]);

  function buildInvoiceLines(rawItems = []) {
    return rawItems.map(it => {
      const pid = Number(it.productId);
      const p = products.find(pr => Number(pr.id) === pid);

      const unitsList = Array.isArray(p?.units) ? p.units : (it.units || []);
      const u = unitsList.find(x => Number(x.unitId) === Number(it.unitId))
        || unitsList.find(x => x.isBaseUnit)
        || {};

      // نسبة الضريبة المحفوظة في الفاتورة أولًا، لا نسبة الصنف الحالية (قد تكون تغيّرت بعد الحفظ)
      const tax = it.taxPercent ?? p?.taxRate ?? 0;
      let storedPrice = parseFloat(it.unitPrice ?? it.price ?? 0);
      let storedDiscount = parseFloat(it.discount || 0);
      // خصم العرض محفوظ داخل الخصم: نفصله ليبقى في الخانة الخصم اليدوي فقط (المرتجع يأخذ الخصم كاملًا)
      let storedPromo = mode === "return" ? 0 : parseFloat(it.promotionDiscount || 0);

      // السيرفر يحفظ السعر والخصم غير شاملين: نحوّلهما معًا عند عرض الأسعار شاملة
      if (priceIncludesVAT) {
        storedPrice = storedPrice * (1 + parseFloat(tax || 0));
        storedDiscount = storedDiscount * (1 + parseFloat(tax || 0));
        storedPromo = storedPromo * (1 + parseFloat(tax || 0));
      }
      storedDiscount = Math.max(0, storedDiscount - storedPromo);

      return {
        productId: pid,
        productName: p?.arabicName ?? p?.nameAr ?? it.productName ?? it.arabicName ?? it.description ?? "",
        description: it.description ?? "",
        unitId: u?.unitId || 0,
        unitName: u?.unitNameAr || "",
        qty: parseFloat(it.quantity || 1).toFixed(decimalPlaces),
        price: storedPrice.toFixed(decimalPlaces),
        discount: storedDiscount.toFixed(decimalPlaces),
        taxRate: parseFloat(tax).toFixed(decimalPlaces),
        unitsList,
        promoDiscount: storedPromo,               // بأساس الأسعار المعروضة
        promoName: storedPromo > 0 ? "عرض" : "",
      };
    });
  }

  function formatDateForInput(value) {
    if (!value) return "";
    const text = String(value);
    return text.length >= 10 ? text.slice(0, 10) : text;
  }

  useEffect(() => {
    const sourceId =
      mode === "edit" ? editingId :
        mode === "return" ? returnOf :
          mode === "fromQuote" ? fromQuoteId :
            mode === "copy" ? copyOfId :
            null;

    if (!sourceId) return;
    if (mode === "copy") {
      if (copyLoadedRef.current) return; // النسخة تُحمَّل مرة واحدة فلا تُمسح تعديلات المستخدم
      if (!productsLoaded) return;      // ننتظر انتهاء تحميل الأصناف (حتى لو كانت القائمة فارغة)
    } else if (products.length === 0) return;

    invoiceApi.getById(sourceId)
      .then(res => {
        const inv = res.data;
        const rawItems = inv.items || inv.invoiceItems || inv.details || [];
        const lines = buildInvoiceLines(rawItems);

        // ── نسخ الفاتورة: نفس العميل والأصناف والدفع والمستودع، برقم وتاريخ جديدين ومسودة
        if (mode === "copy") {
          if (copyLoadedRef.current) return;
          copyLoadedRef.current = true;
          if (!inv || !rawItems.length) {
            console.warn("نسخ الفاتورة: لا توجد أصناف في الاستجابة", inv);
            toast.warning("تم فتح الفاتورة لكن لم تُرجع الخدمة أصنافًا للنسخ");
          } else {
            toast.success(`تم نسخ ${rawItems.length} صنف من الفاتورة رقم ${inv.invoiceNumber}`);
          }
          const today = new Date().toLocaleDateString('en-CA');
          // رصيد العميل المحفوظ في الفاتورة يبقى كما هو ما لم يتغير العميل
          loadedCustomerRef.current = null;
          setForm(f => ({
            ...f,
            invoiceTypeId: inv.invoiceType ?? f.invoiceTypeId,
            issueDate: today,
            referenceNumber: initialFormState.referenceNumber,
            referenceDate: today,
            customerId: inv.customerId ?? f.customerId,
            supplierId: inv.supplierId ?? 0,
            customerPrevBalance: 0,
            stockId: inv.stockId ?? f.stockId,
            toStockId: inv.toStockId ?? 0,
            salesmanId: inv.salesmanId ?? f.salesmanId,
            paymentMethodId: inv.paymentMethodId ?? f.paymentMethodId,
            safeId: inv.safeId ?? 0,
            bankId: inv.bankId ?? 0,
            notes: inv.notes || "",
            discount: parseFloat(0).toFixed(decimalPlaces),
            invoiceStatus: 0,
            paidAmount: 0,
            journalEntryGlobalId: null,
            customHeaders: inv.customHeaders || {},
            lines,
          }));
          setCopySourceNumber(inv.invoiceNumber);
          fetchNextInvoiceNumber(inv.invoiceType ?? form.invoiceTypeId);
          setIsApproved(false);
          setShowApproveButton(false);
          setCanUnapprove(false);
          setIsZatcaLocked(false);
          setZatcaStatus(null);
          return;
        }

        // رصيد العميل المحفوظ في الفاتورة يبقى كما هو ما لم يتغير العميل
        loadedCustomerRef.current = (mode === "return" || mode === "fromQuote") ? null : (inv.customerId ?? 0);
        setForm(f => ({
          ...f,
          invoiceTypeId: mode === "return"
            ? (returnTypeId ? parseInt(returnTypeId, 10) : (inv.invoiceType === 2 ? 4 : 3))
            : mode === "fromQuote"
            ? 1
            : inv.invoiceType,
          invoiceNumber: mode === "fromQuote" ? "" : inv.invoiceNumber,
          issueDate: mode === "fromQuote" ? new Date().toLocaleDateString('en-CA') : formatDateForInput(inv.issueDate),
          referenceNumber: mode === "return" ? inv.invoiceNumber : (inv.referenceNumber ?? inv.invoiceNumber),
          referenceDate: formatDateForInput(mode === "return" ? inv.issueDate : (inv.referenceDate || inv.issueDate)),
          customerId: inv.customerId ?? f.customerId,
          supplierId: mode === "fromQuote" ? 0 : (inv.supplierId ?? 0),
          customerPrevBalance: parseFloat(inv.customerPrevBalance || 0),
          stockId: inv.stockId ?? f.stockId,
          toStockId: inv.toStockId ?? 0,
          salesmanId: inv.salesmanId ?? f.salesmanId,
          paymentMethodId: inv.paymentMethodId ?? f.paymentMethodId,
          safeId: inv.safeId ?? 0,
          bankId: inv.bankId ?? 0,
          notes: inv.notes || "",
          discount: parseFloat(0).toFixed(decimalPlaces), // خصم الفاتورة محفوظ داخل خصومات البنود — لا يُحمَّل مرتين
          invoiceStatus: mode === "fromQuote" || mode === "return" ? 0 : (inv.invoiceStatus ?? 0),
          paidAmount: mode === "fromQuote" || mode === "return" ? 0 : parseFloat(inv.paidAmount || 0),
          journalEntryGlobalId: inv.journalEntryGlobalId, // ✅ Populate from API
          // المرتجع/التحويل من عرض سعر نوع آخر بحقول أخرى — يبدأ فارغًا
          customHeaders: mode === "return" || mode === "fromQuote" ? {} : (inv.customHeaders || {}),
          lines,
        }));

        if (mode === "fromQuote") {
          fetchNextInvoiceNumber(1);
        }

        setIsApproved(mode === "return" ? false : inv.invoiceStatus === 1);
        setShowApproveButton(mode === "edit" ? inv.invoiceStatus === 0 : false);
        setCanUnapprove(mode === "return" ? false : !!inv.canUnapprove);
        setIsZatcaLocked(!!inv.isZatcaLocked);
        setZatcaStatus(inv.zatcaStatus || (inv.isSentToZatca ? "REPORTED" : null));
      })
      .catch(err => {
        console.error(err);
        if (mode === "copy") toast.error("تعذر تحميل الفاتورة المراد نسخها: " + (err?.response?.status || err?.message || ""));
      });
  }, [mode, editingId, returnOf, returnTypeId, fromQuoteId, copyOfId, products, productsLoaded, decimalPlaces, priceIncludesVAT]);

  // ───────────── أسعار الوحدات ─────────────
  // الـ API يرجع سعر الوحدة باسم salprice / purchprice (وسعر الصنف saleprice / purchprice)
  const priceNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
  const findProduct = (productId) => products.find(pr => Number(pr.id) === Number(productId));

  // سعر الوحدة؛ وإن لم يُحدد: سعر الوحدة الأساسية × معامل هذه الوحدة
  function unitPriceOf(u, p, kind) {
    const read = (x) => kind === "sale"
      ? priceNum(x?.salprice ?? x?.saleprice ?? x?.salPrice)
      : priceNum(x?.purchprice ?? x?.purchaseprice ?? x?.purchPrice);
    const direct = read(u);
    if (direct > 0) return direct;

    const units = Array.isArray(p?.units) ? p.units : [];
    const base = units.find(x => x.isBaseUnit);
    const baseFactor = priceNum(base?.factor) || 1;
    const factor = priceNum(u?.factor) || 1;
    const basePrice = read(base) || (kind === "sale" ? priceNum(p?.saleprice) : priceNum(p?.purchprice ?? p?.purchaseprice));
    return basePrice * factor / baseFactor;
  }

  // الإدخال والتسويات والمناقلة ومرتجع المشتريات بسعر الشراء، والباقي بسعر البيع
  const usesPurchasePrice = isInbound || isStockAdjustment || isTransfer || form.invoiceTypeId === 4;

  function pickUnitPriceForDirection(u, p) {
    const product = p || findProduct(u?.productId);
    const price = unitPriceOf(u, product, usesPurchasePrice ? "purchase" : "sale");
    return parseFloat(price || 0).toFixed(decimalPlaces);
  }

  function getPurchasePrice(u, p) {
    return unitPriceOf(u, p, "purchase");
  }

  
// ======================================================================
// حساب الإجماليات
// ⚠️ ln.taxRate مخزّن ككسر عشري (0.15 = 15%) — نفس ما يستخدمه جدول البنود
//    والحفظ والتصدير، فلا يُقسم على 100 هنا.
// خصم الفاتورة يُوزَّع على السطور بنسبة قيمة كل سطر، ثم تُحسب الضريبة
// على المبلغ بعد الخصم (يعمل صحيحًا حتى مع اختلاف نسب الضريبة بين السطور).
// ======================================================================
// ── نوع فاتورة "بدون ضريبة" (إعدادات البرنامج) ──
// يُطبَّق على الفواتير الجديدة فقط — الفواتير المحفوظة تبقى بضريبتها كما هي
const vatExempt = !!settings.isVatExempt && (mode === "new" || mode === "copy" || mode === "fromQuote");
useEffect(() => {
  if (!vatExempt) return;
  if (priceIncludesVAT) setPriceIncludesVAT(false);
  if (form.lines.some(ln => (parseFloat(ln.taxRate) || 0) !== 0)) {
    setForm(f => ({ ...f, lines: f.lines.map(ln => ((parseFloat(ln.taxRate) || 0) !== 0 ? { ...ln, taxRate: (0).toFixed(decimalPlaces) } : ln)) }));
  }
}, [vatExempt, form.lines, priceIncludesVAT, decimalPlaces]);

// أساس نسبة خصم الفاتورة: إجمالي السطور بعد خصومات السطور (نفس أساس توزيع الخصم)
const docDiscountBase = form.lines.reduce((s, ln) =>
  s + (parseFloat(ln.qty) || 0) * (parseFloat(ln.price) || 0) - (parseFloat(ln.discount) || 0), 0);

// فاتورة أخرى (جديدة/تنقل/تحميل) ← نرجع لوضع الخصم بالمبلغ
useEffect(() => { setDocDiscountPct(null); }, [form.invoiceNumber]);

// النسبة ثابتة: يُعاد حساب مبلغ خصم الفاتورة كلما تغيّرت السطور
useEffect(() => {
  if (docDiscountPct === null) return;
  const pct = Math.min(Math.max(parseFloat(docDiscountPct) || 0, 0), 100);
  const amount = (Math.max(docDiscountBase, 0) * pct / 100).toFixed(decimalPlaces);
  setForm(f => (f.discount === amount ? f : { ...f, discount: amount }));
}, [docDiscountPct, docDiscountBase, decimalPlaces]);

// النسبة المعروضة: المُدخلة، أو المحسوبة من مبلغ الخصم
const docDiscountPctShown = docDiscountPct !== null
  ? docDiscountPct
  : (docDiscountBase > 0 && (parseFloat(form.discount) || 0) > 0
      ? String(Math.round((parseFloat(form.discount) || 0) / docDiscountBase * 10000) / 100)
      : "");

// ── تحويل سطور الشاشة لبنود الخادم: أسعار غير شاملة + الخصم اليدوي ونصيب السطر من خصم الفاتورة اليدوي
//    (يُستخدم للحفظ ولمعاينة العروض — نفس الأرقام في الحالتين)
function toApiItems(lines, docDiscountValue) {
  const docDiscountForAPI = parseFloat(docDiscountValue) || 0;
  const linesAfterLineDisc = lines.map(ln =>
    (parseFloat(ln.qty) || 0) * (parseFloat(ln.price) || 0) - (parseFloat(ln.discount) || 0)
  );
  const sumAfterLineDisc = linesAfterLineDisc.reduce((s, v) => s + v, 0);

  return lines.map((ln, idx) => {
    const taxRate = parseFloat(ln.taxRate) || 0;
    const share = sumAfterLineDisc > 0
      ? docDiscountForAPI * (linesAfterLineDisc[idx] / sumAfterLineDisc)
      : 0;

    let unitPriceForAPI = parseFloat(ln.price) || 0;
    let discountForAPI = (parseFloat(ln.discount) || 0) + share;

    // السيرفر يستقبل أسعارًا غير شاملة: نحوّل السعر والخصم معًا
    if (priceIncludesVAT) {
      unitPriceForAPI = unitPriceForAPI / (1 + taxRate);
      discountForAPI = discountForAPI / (1 + taxRate);
    }

    return {
      productId: ln.productId,
      unitId: ln.unitId,
      quantity: parseFloat(ln.qty),
      unitPrice: unitPriceForAPI,
      discount: discountForAPI,
      taxPercent: taxRate,
      description: ln.description ?? "",
    };
  });
}

// ── العروض: معاينة من الخادم (نفس محرّك الحفظ) كلما تغيّرت السطور أو الخصم
//    القيم تُقرأ من مرجع محدَّث كل رسم، والتشغيل يتبع "بصمة" ما يؤثر على العروض فقط
//    (كتابة نتيجة العرض في السطور لا تغيّر البصمة فلا تتكرر الطلبات)
const promoCtxRef = useRef(null);
promoCtxRef.current = { form, branchId, priceIncludesVAT, decimalPlaces, toApiItems };
const promoSignature = JSON.stringify([
  form.invoiceTypeId, branchId, priceIncludesVAT, form.discount,
  form.lines.map(ln => [ln.productId, ln.unitId, ln.qty, ln.price, ln.discount, ln.taxRate]),
]);
useEffect(() => {
  const ctx = promoCtxRef.current;
  if (viewMode || !ctx.form.invoiceTypeId || !promoSignature) return undefined;
  const indexed = ctx.form.lines.map((ln, i) => ({ ln, i })).filter(x => x.ln.productId && parseFloat(x.ln.qty) > 0);
  if (!indexed.length) {
    setForm(f => (f.lines.some(l => l.promoDiscount)
      ? { ...f, lines: f.lines.map(l => (l.promoDiscount ? { ...l, promoDiscount: 0, promoName: "", promotionId: null } : l)) }
      : f));
    return undefined;
  }

  let alive = true;
  const timer = setTimeout(async () => {
    try {
      const items = ctx.toApiItems(indexed.map(x => x.ln), ctx.form.discount)
        .map((it, k) => ({ ...it, key: indexed[k].i }));
      const { data } = await axios.post("/Promotions/evaluate", {
        branchId: ctx.branchId, invoiceTypeId: ctx.form.invoiceTypeId, items,
      });
      if (!alive) return;
      const byKey = new Map((data?.lines || []).map(l => [Number(l.key), l]));
      const dp = ctx.decimalPlaces;
      setForm(f => ({
        ...f,
        lines: f.lines.map((ln, i) => {
          const r = byKey.get(i);
          const totalExcl = Number(r?.total) || 0;
          const tax = parseFloat(ln.taxRate) || 0;
          const shown = ctx.priceIncludesVAT ? totalExcl * (1 + tax) : totalExcl;
          const promoDiscount = Math.round(shown * 10 ** dp) / 10 ** dp;
          const promoHint = r?.hintLineQty > 0 ? { qty: Number(r.hintLineQty), name: r.hintPromotionName || "" } : null;
          if ((ln.promoDiscount || 0) === promoDiscount && (ln.promotionId ?? null) === (r?.promotionId ?? null)
            && (ln.promoHint?.qty ?? 0) === (promoHint?.qty ?? 0)) return ln;
          return { ...ln, promoDiscount, promoHint, promoName: promoDiscount > 0 ? (r?.promotionName || "عرض") : "", promotionId: promoDiscount > 0 ? r?.promotionId : null };
        }),
      }));
    } catch {
      // المعاينة اختيارية — الخادم يطبّق العروض عند الحفظ على أي حال
    }
  }, 350);
  return () => { alive = false; clearTimeout(timer); };
}, [promoSignature, viewMode]);

const calculateTotals = () => {
  const docDiscount = parseFloat(form.discount) || 0;

  const lines = form.lines.map((ln) => {
    const qty = parseFloat(ln.qty) || 0;
    const price = parseFloat(ln.price) || 0;
    const lineDisc = parseFloat(ln.discount) || 0;
    const taxRate = parseFloat(ln.taxRate) || 0;
    const promo = parseFloat(ln.promoDiscount) || 0;
    const gross = qty * price;
    return { gross, lineDisc, taxRate, promo, afterLineDisc: gross - lineDisc };
  });

  const subtotal = lines.reduce((s, l) => s + l.gross, 0);
  const totalLineDiscounts = lines.reduce((s, l) => s + l.lineDisc, 0);
  const totalPromo = lines.reduce((s, l) => s + l.promo, 0);
  const sumAfterLineDisc = lines.reduce((s, l) => s + l.afterLineDisc, 0);

  let totalTaxable = 0;
  let totalVAT = 0;
  let net = 0;

  lines.forEach((l) => {
    // نصيب السطر من خصم الفاتورة
    const share = sumAfterLineDisc > 0 ? docDiscount * (l.afterLineDisc / sumAfterLineDisc) : 0;
    const amount = l.afterLineDisc - share - l.promo;

    if (priceIncludesVAT) {
      // السعر شامل: المبلغ يتضمن الضريبة
      const taxable = amount / (1 + l.taxRate);
      totalTaxable += taxable;
      totalVAT += amount - taxable;
      net += amount;
    } else {
      // السعر غير شامل: الضريبة تُضاف على المبلغ
      const vat = amount * l.taxRate;
      totalTaxable += amount;
      totalVAT += vat;
      net += amount + vat;
    }
  });

  return {
    subtotal,
    totalLineDiscounts,
    totalPromo,
    totalDiscount: totalLineDiscounts + docDiscount + totalPromo,
    totalAfterDiscount: totalTaxable, // المبلغ الخاضع للضريبة (قبل الضريبة)
    totalVAT,
    net,
  };
};

const totals = calculateTotals();

  const defaultInvoiceStatus = settings.autoApproveInvoices ? 1 : 0;

  useEffect(() => {
    if (form.paymentMethodId !== creditMethodId) {
      setForm(f => ({
        ...f,
        paidAmount: parseFloat(totals.net.toFixed(decimalPlaces)),
      }));
    }
  }, [form.paymentMethodId, totals.net, decimalPlaces, creditMethodId]);

  
// ── الحقول المخصصة لنوع الفاتورة (تُعرَّف من شاشة أنواع الفواتير)
const [customFields, setCustomFields] = useState([]);
useEffect(() => {
  const typeId = Number(form.invoiceTypeId);
  if (!companyId || !typeId) { setCustomFields([]); return; }
  let alive = true;
  axios.get('/InvoiceFields', { params: { companyId, invoiceTypeId: typeId } })
    .then(r => { if (alive) setCustomFields(Array.isArray(r.data) ? r.data : []); })
    .catch(() => { if (alive) setCustomFields([]); });
  return () => { alive = false; };
}, [companyId, form.invoiceTypeId]);

const setCustomValue = (fieldName, value) =>
  setForm(f => ({ ...f, customHeaders: { ...(f.customHeaders || {}), [fieldName]: value } }));

// ── رصيد العميل السابق: من كشف حساب العميل عند اختياره (الخادم يحسبه أيضًا عند الحفظ)
useEffect(() => {
  const cid = Number(form.customerId) || 0;
  if (loadedCustomerRef.current !== null && cid === Number(loadedCustomerRef.current)) return;
  if (!cid || !companyId) { setForm(f => (f.customerPrevBalance ? { ...f, customerPrevBalance: 0 } : f)); return; }
  let alive = true;
  axios.get(`/Invoice/customer-balance/${cid}`, { params: { companyId, excludeInvoiceId: mode === "edit" ? editingId : undefined } })
    .then(r => {
      if (!alive) return;
      const balance = Number(r.data?.balance) || 0;
      setForm(f => (Number(f.customerId) === cid ? { ...f, customerPrevBalance: balance } : f));
    })
    .catch(() => {});
  return () => { alive = false; };
}, [form.customerId, companyId, mode, editingId]);

function handleChange(e) {
  const { name, value } = e.target;
  let newValue = value;

  if (
    [
      "paymentMethodId",
      "safeId",
      "bankId",
      "customerId",
      "supplierId",
      "stockId",
      "toStockId",
      "salesmanId",
      "invoiceStatus",
      "paidAmount",
    ].includes(name)
  ) {
    newValue = value === "" ? "" : +value;
  } else if (name === "discount") {
    // الاحتفاظ بالنص أثناء الكتابة دون تحويله أو إضافة أصفار
    newValue = value;
  }

  setForm(f => ({ ...f, [name]: newValue }));
}


  function handleAddLine() {
    setForm(f => ({
      ...f,
      lines: [
        ...f.lines,
        {
          productId: 0,
          productName: "",
          description: "",
          unitId: 0,
          unitName: "",
          qty: parseFloat(1).toFixed(decimalPlaces),
          price: parseFloat(0).toFixed(decimalPlaces),
          discount: parseFloat(0).toFixed(decimalPlaces),
          taxRate: parseFloat(0).toFixed(decimalPlaces),
          unitsList: [],
        },
      ],
    }));
  }

  // ── فاتورة جديدة: سطر فارغ جاهز للبحث عن الصنف مباشرة ──
  const autoLineFocusedRef = useRef(false);
  useEffect(() => {
    if (viewMode || mode !== "new" || !form.invoiceTypeId || !productsLoaded) return;
    if (form.lines.length > 0) return;
    handleAddLine();
    if (!autoLineFocusedRef.current) {
      autoLineFocusedRef.current = true;   // التركيز التلقائي مرة واحدة عند فتح الصفحة
      const first = getFirstLineField();
      if (first) pendingLineFocusRef.current = { rowIndex: 0, field: first };
    }
  }, [viewMode, mode, form.invoiceTypeId, productsLoaded, form.lines.length]);

  // ───────────── التنقل بين حقول السطور (Enter / Tab) ─────────────
  // الترتيب: الصنف ← الكمية ← السعر ← الخصم ← سطر جديد (الحقول الظاهرة فقط)
  // الوصف والوحدة خارج التسلسل (تُعدَّل بالنقر)، و Enter منهما ينتقل إلى الكمية
  const formRef = useRef(form);
  formRef.current = form;

  // Enter/Tab بين حقول السطر بنفس ترتيب الأعمدة في الجدول
  const LINE_NAV_FIELDS = { ProductId: "productName", Quantity: "qty", UnitPrice: "price", Discount: "discount" };
  const lineFieldOrder = () => itemOrder
    .filter(k => LINE_NAV_FIELDS[k] && visibleItemFields[k])
    .map(k => LINE_NAV_FIELDS[k]);

  function moveFromLineField(rowIndex, field, backwards = false) {
    const order = lineFieldOrder();
    const lines = formRef.current.lines;
    let r = rowIndex;
    let i = order.indexOf(field);
    // الوصف/الوحدة: نعاملهما كأنهما بعد الصنف مباشرة (التالي = الكمية، السابق = الصنف)
    if (i === -1) i = backwards ? Math.max(order.indexOf("productName"), 0) + 1 : Math.max(order.indexOf("productName"), 0);

    // نتجاوز الحقول المعطّلة/غير الموجودة
    for (let guard = 0; guard < order.length * 2; guard++) {
      if (backwards) {
        i -= 1;
        if (i < 0) { if (r === 0) return; r -= 1; i = order.length - 1; }
      } else {
        i += 1;
        if (i >= order.length) {
          // آخر حقل: السطر التالي، أو سطر جديد إذا كان هذا آخر سطر وفيه صنف
          if (r < lines.length - 1) { r += 1; i = 0; }
          else {
            if (isBlankLine(lines[r])) return;
            handleAddLine();
            pendingLineFocusRef.current = { rowIndex: lines.length, field: order[0] };
            return;
          }
        }
      }
      if (focusLineField(r, order[i])) return;
    }
  }

  function handleLineFieldKeyDown(e, rowIndex, field) {
    const isTab = e.key === "Tab";
    if (e.key !== "Enter" && !isTab) return;
    e.preventDefault();
    moveFromLineField(rowIndex, field, isTab && e.shiftKey);
  }

  // البحث عن صنف بما كُتب: باركود أو رمز مطابق تمامًا، أو اسم مطابق، أو نتيجة وحيدة لكلمات البحث
  function resolveProduct(text) {
    const raw = String(text ?? "").trim();
    const q = normalizeSearch(raw);
    if (!q) return { product: null, count: 0 };
    // باركود وحدة غير أساسية (مثل الكرتون): نختار الصنف بهذه الوحدة
    for (const { p } of productSearchIndex) {
      const unit = Array.isArray(p.units) ? p.units.find(u => !u.isBaseUnit && String(u.barcode ?? "") === raw) : null;
      if (unit) return { product: p, unit, count: 1 };
    }
    const exact = productSearchIndex.find(({ p }) =>
      String(p.barcode ?? "") === raw ||
      (Array.isArray(p.units) && p.units.some(u => String(u.barcode ?? "") === raw)) ||
      normalizeSearch(p.code) === q ||
      normalizeSearch(p.arabicName) === q);
    if (exact) return { product: exact.p, count: 1 };
    const words = q.split(" ");
    const matches = productSearchIndex.filter(x => words.every(w => x.text.includes(w)));
    return { product: matches.length === 1 ? matches[0].p : null, count: matches.length };
  }

  // Enter / Tab في خانة الصنف: يختار الصنف ثم ينتقل للحقل التالي
  function handleProductKeyDown(e, rowIndex) {
    const isTab = e.key === "Tab";
    if (e.key !== "Enter" && !isTab) return;
    if (isTab && e.shiftKey) { e.preventDefault(); moveFromLineField(rowIndex, "productName", true); return; }
    e.preventDefault();
    const input = e.currentTarget;

    // ننتظر لحظة حتى يُطبَّق اختيار القائمة المنسدلة (datalist) على الحقل
    setTimeout(async () => {
      const ln = formRef.current.lines[rowIndex];
      const text = input.value;

      // الصنف مختار أصلًا ولم يتغير النص
      if (ln?.productId && ln.productName === text) { moveFromLineField(rowIndex, "productName"); return; }
      if (!String(text).trim()) {
        // سطر فارغ: Tab ينقل للحقل التالي، Enter لا يفعل شيئًا
        if (isTab) moveFromLineField(rowIndex, "productName");
        return;
      }

      const { product, unit, count } = resolveProduct(text);
      if (!product) {
        toast.info(count > 1 ? `${count} أصناف مطابقة — اكتب أكثر أو اختر من القائمة` : "لا يوجد صنف مطابق");
        return;
      }

      await handleLineChange(rowIndex, "productName", product.arabicName);
      // ننتقل فقط إذا قُبل الصنف (قد يُرفض لعدم توفر الكمية)
      setTimeout(() => {
        if (unit && formRef.current.lines[rowIndex]?.productId === product.id) {
          setForm(f => {
            const lines = [...f.lines];
            lines[rowIndex] = { ...lines[rowIndex], unitId: unit.unitId, unitName: unit.unitNameAr || "", price: pickUnitPriceForDirection(unit, product) };
            return { ...f, lines };
          });
        }
        if (formRef.current.lines[rowIndex]?.productId) moveFromLineField(rowIndex, "productName");
      }, 0);
    }, 0);
  }

  useEffect(() => {
    if (!pendingLineFocusRef.current) return;

    const { rowIndex, field } = pendingLineFocusRef.current;
    const timer = setTimeout(() => {
      if (focusLineField(rowIndex, field)) {
        pendingLineFocusRef.current = null;
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [form.lines.length, visibleItemFields.ProductId, visibleItemFields.Description, visibleItemFields.Unit, visibleItemFields.Quantity, visibleItemFields.UnitPrice, visibleItemFields.Discount]);

  function handleRemoveLine(i) {
    setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));
  }

  // أرصدة المستودع الحالي: طلب واحد عند فتح الإدراج المتعدد
  useEffect(() => {
    if (!bulkOpen || !companyId || !form.stockId) { if (!bulkOpen) setBulkOnlySelected(false); return; }
    let alive = true;
    setBulkBalancesLoading(true);
    axios.get("/StockCount/balances", { params: { companyId, stockId: form.stockId } })
      .then(r => { if (alive) setBulkBalances(Object.fromEntries((r.data || []).map(b => [b.productId, Number(b.qty) || 0]))); })
      .catch(() => { if (alive) setBulkBalances({}); })
      .finally(() => { if (alive) setBulkBalancesLoading(false); });
    return () => { alive = false; };
  }, [bulkOpen, companyId, form.stockId]);

  // تبديل اختيار صنف للإدراج المتعدد
  function toggleBulkProduct(productId, checked) {
    setBulkSelected(prev => {
      const next = { ...prev };
      if (checked) {
        if (next[productId] == null) next[productId] = 1;
      } else {
        delete next[productId];
      }
      return next;
    });
  }

  // تحديث كمية صنف محدد في الإدراج المتعدد
  function setBulkProductQty(productId, qtyRaw) {
    const qty = parseFloat(qtyRaw) || 0;
    setBulkSelected(prev => ({ ...prev, [productId]: qty }));
  }

  // تنفيذ الإدراج المتعدد للأصناف المحددة
  // selection اختياري: النقر المزدوج يمرّر التحديد مع الصنف المنقور
  function handleBulkInsert(selection = bulkSelected) {
    const bulkSelected = selection;
    const selectedIds = Object.keys(bulkSelected).map(Number);
    if (selectedIds.length === 0) {
      toast.warning("اختر أصنافًا أولاً");
      return;
    }

    const newLines = selectedIds.map(pid => {
      const p = products.find(pr => Number(pr.id) === pid);
      if (!p) return null;
      const unitsList = Array.isArray(p.units) ? p.units : [];
      const bu = unitsList.find(u => u.isBaseUnit) || {};
      const qty = parseFloat(bulkSelected[pid] ?? 1) || 1;
      return {
        productId: pid,
        productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
        description: p?.description ?? "",
        unitId: bu?.unitId || 0,
        unitName: bu?.unitNameAr || "",
        qty: parseFloat(qty).toFixed(decimalPlaces),
        price: pickUnitPriceForDirection(bu, p),
        discount: parseFloat(0).toFixed(decimalPlaces),
        taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
        unitsList,
      };
    }).filter(Boolean);

    setForm(f => ({ ...f, lines: [...f.lines.filter(ln => !isBlankLine(ln)), ...newLines] }));
    setBulkSelected({});
    setBulkSearch("");
    setBulkOpen(false);
    toast.success("✅ تم إدراج الأصناف المحددة");
  }

  async function handleLineChange(idx, field, raw) {
    const lines = [...form.lines];
    const ln = { ...lines[idx] };

    if (field === "productName") {
      ln.productName = raw;
      const p = pickableProducts.find(x => x.arabicName === raw);
      if (!p) {
        lines[idx] = ln;
        setForm(f => ({ ...f, lines }));
        return;
      }

      if (isOutbound && !p.isService) {
        try {
          const response = await axios.get(
            `/StockMovements/stock-balance/${companyId}`,
            { params: { productId: p.id, branchId, stockId: form.stockId } }
          );
          const availableQty = parseFloat(response.data ?? 0);
          if (!settings.allowNegativeSales && availableQty <= 0) {
            toast.error(t('invoice.messages.quantityNotAvailable'));
            return;
          }
        } catch {
          if (!settings.allowNegativeSales) {
            toast.warning(t('invoice.messages.cannotVerifyQuantity'));
            return;
          }
        }
      }

      ln.productId = p.id;
      ln.taxRate = parseFloat(p.taxRate || 0).toFixed(decimalPlaces);
      ln.unitsList = p.units || [];
      const bu = ln.unitsList.find(u => u.isBaseUnit) || {};
      ln.unitId = bu.unitId || 0;
      ln.unitName = bu.unitNameAr || "";
      // Default description from product if available
      ln.description = p?.description ?? ln.description ?? "";

      ln.price = pickUnitPriceForDirection(bu, p, ln.taxRate);
      ln.discount = parseFloat(0).toFixed(decimalPlaces);
      ln.qty = parseFloat(1).toFixed(decimalPlaces);
    } else if (field === "unitId") {
      const u = ln.unitsList.find(x => x.unitId === +raw) || {};
      ln.unitId = u.unitId || 0;
      ln.unitName = u.unitNameAr || "";
      // ✅ كان يُمرَّر null بدل الصنف، وسعر الوحدة يُقرأ باسم خاطئ (saleprice) فيرجع صفرًا
      ln.price = pickUnitPriceForDirection(u, findProduct(ln.productId));
    } else if (field === "qty") {
      // السماح بالقيمة كما هي (نص) للسماح بالكتابة الحرة
      ln.qty = raw;
    } else if (field === "price") {
      // السماح بالقيمة كما هي (نص) للسماح بالكتابة الحرة
      ln.price = raw;
    } else if (field === "discount") {
      // السماح بالقيمة كما هي (نص) للسماح بالكتابة الحرة
      ln.discount = raw;
    } else if (field === "taxRate") {
      ln.taxRate = parseFloat(raw).toFixed(decimalPlaces);
    } else if (field === "description") {
      ln.description = raw;
    }

    lines[idx] = ln;
    setForm(f => ({ ...f, lines }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (viewMode || isSaving) return;

    // السطور الفارغة (مثل سطر البحث التلقائي) لا تُحفظ
    const submitLines = form.lines.filter(ln => !isBlankLine(ln));
    if (submitLines.length === 0) {
      toast.error("أضف صنفًا واحدًا على الأقل");
      return;
    }

    if (isCustomerParty && (!form.customerId || form.customerId === 0)) {
      toast.error(t('invoice.messages.selectCustomerFirst'));
      return;
    }
    if (isSupplierParty && (!form.supplierId || form.supplierId === 0)) {
      toast.error(t('invoice.messages.selectSupplierFirst'));
      return;
    }
    if (isTransfer) {
      if (!form.toStockId) { toast.error("اختر المستودع المحوَّل إليه"); return; }
      if (Number(form.toStockId) === Number(form.stockId)) { toast.error("لا يمكن المناقلة إلى نفس المستودع"); return; }
    }

    const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
    if (form.paymentMethodId === cashId && !form.safeId) {
      return alert(t('invoice.messages.selectSafe'));
    }

    const productIds = new Set();
    for (let ln of submitLines) {
      const qty = parseFloat(ln.qty);
      if (!ln.productId || isNaN(qty) || qty <= 0) {
        toast.error(`${t('invoice.messages.invalidQuantity')} "${ln.productName}"`);
        return;
      }
      if (productIds.has(ln.productId)) {
        toast.error(`${t('invoice.messages.duplicateItem')} "${ln.productName}"`);
        return;
      }
      productIds.add(ln.productId);
    }

    if (isOutbound && !settings.allowNegativeSales) {
      let hasError = false;
      for (let ln of submitLines) {
        if (isServiceProduct(ln.productId)) continue;   // الخدمة بلا مخزون
        const qty = parseFloat(ln.qty);
        try {
          const res = await axios.get(
            `/StockMovements/stock-balance/${companyId}`,
            { params: { productId: ln.productId, branchId, stockId: form.stockId } }
          );
          const availableQty = parseFloat(res.data ?? 0);
          if (qty > availableQty) {
            toast.error(`${t('invoice.messages.insufficientQuantity')} "${ln.productName}" (${availableQty})`);
            hasError = true;
          }
        } catch {
          toast.error(`${t('invoice.messages.cannotCheckBalance')} "${ln.productName}"`);
          hasError = true;
        }
      }
      if (hasError) return;
    }

    // ── خصم الفاتورة يُوزَّع على البنود (نفس حساب الإجماليات) — خصم العروض يحسبه الخادم ويضيفه بنفسه
    const payloadItems = toApiItems(submitLines, form.discount);

    let invoiceStatusToSend = form.invoiceStatus;
    if ((mode === "new" || mode === "copy") && settings.autoApproveInvoices) {
      invoiceStatusToSend = 1;
    }

    // الحقول المخصصة الإلزامية
    const missingField = customFields.find(cf => {
      const v = String(form.customHeaders?.[cf.fieldName] ?? '').trim();
      return cf.isRequired && (cf.dataType === 'bool' ? v !== 'true' : !v);
    });
    if (missingField) {
      toast.error(`الحقل "${missingField.displayName}" مطلوب`);
      document.getElementById(`cf-${missingField.fieldName}`)?.focus();
      return;
    }

    const payload = {
      applyPromotions: true,   // الخادم يحسب العروض ويضيفها للخصم (items تحمل الخصم اليدوي فقط)
      // فقط حقول النوع الحالي (المفاتيح القديمة من نوع آخر لا تُرسل)
      customHeaders: Object.fromEntries(customFields.map(cf => [cf.fieldName, String(form.customHeaders?.[cf.fieldName] ?? '')])),
      companyId,
      branchId,
      userId: userId || 0, // ✅ Send userId
      invoiceTypeId: form.invoiceTypeId,
      invoiceNumber: form.invoiceNumber,
      issueDate: new Date(form.issueDate).toISOString(),
      referenceNumber: form.referenceNumber,
      referenceDate: new Date(form.referenceDate).toISOString(),
      customerId: isCustomerParty ? form.customerId : 0,
      supplierId: isSupplierParty ? form.supplierId : 0,
      customerPrevBalance: parseFloat(form.customerPrevBalance),
      stockId: form.stockId,
      toStockId: form.invoiceTypeId === 12 ? form.toStockId : null,
      salesmanId: form.salesmanId,
      paymentMethodId: form.paymentMethodId,
      safeId: form.safeId,
      bankId: form.bankId,
      notes: form.notes,
      discount: 0, // خصم الفاتورة مُوزَّع داخل خصومات البنود
      invoiceStatus: invoiceStatusToSend,
      paidAmount: parseFloat(form.paidAmount),
      totals: {
        subtotal: totals.subtotal,
        totalAfterDiscount: totals.totalAfterDiscount,
        totalVAT: totals.totalVAT,
        net: totals.net,
      },
      items: payloadItems,
    };

    if (mode === "edit") {
      payload.InvGlobalID = editingId;
    }

    try {
      setIsSaving(true);

      if (mode === "return") {
        // ✅ كان يُجبر 3 (مرتجع مبيعات) عند غياب returnTypeId — فمرتجع الشراء يُحفظ مرتجع بيع ويطلب عميلًا
        payload.invoiceTypeId = returnTypeId ? parseInt(returnTypeId, 10) : (form.invoiceTypeId || 3);
        payload.referenceNumber = form.referenceNumber || form.invoiceNumber;
        payload.referenceDate = new Date().toISOString();
        payload.invoiceStatus = 0;
        payload.paidAmount = 0;
      }

      console.log("🚀 Sending payload:", payload, "mode:", mode);
      const res =
        mode === "edit"
          ? await invoiceApi.update(editingId, payload)
          : await invoiceApi.create(payload);

      const invGlobalId = mode === "edit" ? editingId : res.data.invGlobalID;
      const canApproveAfterSave = invoiceStatusToSend !== 1;

      if (settings.autoPrintAfterSave) {
        toast.success(t('invoice.messages.invoiceSaved'));
        setIsSaving(false);
        // الطباعة المباشرة (فتح نافذة الطباعة تلقائياً)
        window.open(`/invoices/print/${invGlobalId}?auto=true`, "_blank");
        
        // إذا كان هناك اعتماد تلقائي، لا حاجة لفعله هنا لأنه تم إرسال الحالة في الـ payload
        // فقط نقوم بتصفير النموذج
        resetForm();
      } else {
        setIsSaving(false);
        const result = await Swal.fire({
          title: t('invoice.messages.invoiceSaved'),
          showCancelButton: true,
          showDenyButton: canApproveAfterSave,
          cancelButtonText: t('invoice.buttons.new'),
          confirmButtonText: t('invoice.buttons.print'),
          denyButtonText: canApproveAfterSave ? t('invoice.buttons.approveInvoice') : undefined,
          // الترتيب من اليمين: جديد ← اعتماد ← طباعة (مهما كان اتجاه الصفحة)
          didOpen: (popup) => {
            const actions = popup.querySelector('.swal2-actions');
            if (actions) actions.style.direction = 'rtl';
            const order = { '.swal2-cancel': 1, '.swal2-deny': 2, '.swal2-confirm': 3 };
            Object.entries(order).forEach(([sel, n]) => {
              const btn = popup.querySelector(sel);
              if (btn) btn.style.order = String(n);
            });
          },
        });

        if (result.isConfirmed) {
          window.open(`/invoices/print/${invGlobalId}`, "_blank");
          resetForm();
        } else if (result.isDenied && canApproveAfterSave) {
          await handleApprove(invGlobalId);
          resetForm();
        } else {
          resetForm();
        }
      }
    } catch (err) {
      setIsSaving(false);
      console.error("❌ Save error:", err);

      const serverError =
        err.response?.data?.errors ||
        err.response?.data ||
        err.message;
      toast.error(
        t('invoice.messages.saveError') + ": " + JSON.stringify(serverError)
      );
    }
  }

  // تطبيق الإعدادات الافتراضية من إعدادات البرنامج لنوع الفاتورة المحدد
  async function applyProgramDefaults(typeId) {
    if (!companyId || !typeId) return;
    try {
      const r = await axios.get("/ProgramSettings/by-company-and-type", {
        params: { companyId, invoiceTypeId: typeId },
      });
      const s = r.data;
      setSettings(s);

      const cashIdLocal = methods.find(m => m.nameAr === "نقدي")?.id;
      const defaultPM = s.defaultPaymentMethodId ?? 0;
      setPriceIncludesVAT(!!s.isPriceIncludesVAT);

      // Priority: Restricted Single > Settings > Default
      // New Logic:
      // If allowedSafeIds has items, check if default safe is in it. If not, pick first allowed.
      // If restrictedSafeId (legacy single) exists, it overrides everything.
      
      let finalSafeId = s.defaultSafeId || 0;
      if (restrictedSafeId) {
          finalSafeId = restrictedSafeId;
      } else if (allowedSafeIds.length > 0) {
          // If default is not allowed, pick first allowed
          if (!allowedSafeIds.includes(finalSafeId)) {
              finalSafeId = allowedSafeIds[0];
          }
      }

      let finalStockId = s.defaultStockId || 0;
      if (restrictedStockId) {
          finalStockId = restrictedStockId;
      } else if (allowedStockIds.length > 0) {
          if (!allowedStockIds.includes(finalStockId)) {
              finalStockId = allowedStockIds[0];
          }
      }

      loadedCustomerRef.current = null;   // فاتورة جديدة: الرصيد من كشف حساب العميل
      setForm(f => ({
        ...f,
        customerId: s.defaultCustomerId ?? 0,
        paymentMethodId: defaultPM || 0,
        safeId: defaultPM === cashIdLocal ? finalSafeId : 0,
        bankId: defaultPM !== cashIdLocal ? (s.defaultBankId ?? 0) : 0,
        stockId: finalStockId,
        invoiceStatus: mode === "new" ? (s.autoApproveInvoices ? 1 : 0) : f.invoiceStatus,
      }));
    } catch (err) {
      console.error("Failed to apply program defaults", err);
    }
  }

  const handleApprove = async (idToApprove = editingId) => {
    try {
      setIsSaving(true);
      await invoiceApi.approve(idToApprove);
      toast.success(t('invoice.messages.invoiceApproved'));
      setShowApproveButton(false);
      setIsApproved(true);
      setCanUnapprove(!isZatcaLocked);
      setForm(f => ({ ...f, invoiceStatus: 1 }));
      
      // Update Zatca Status locally to reflect success immediately
      setZatcaStatus("REPORTED"); 
    } catch {
      toast.error(t('invoice.messages.approvalFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnapprove = async () => {
    if (!editingId || isSaving || !canUnapprove) return;

    const result = await Swal.fire({
      title: "إلغاء اعتماد الفاتورة؟",
      text: "سيتم إرجاع الفاتورة إلى مسودة حتى تتمكن من تعديلها.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "نعم، إلغاء الاعتماد",
      cancelButtonText: "إلغاء",
    });

    if (!result.isConfirmed) return;

    try {
      setIsSaving(true);
      await invoiceApi.unapprove(editingId);
      const res = await invoiceApi.getById(editingId);
      const inv = res.data;
      const rawItems = inv.items || inv.invoiceItems || inv.details || [];
      const lines = buildInvoiceLines(rawItems);

      // رصيد العميل المحفوظ في الفاتورة يبقى كما هو ما لم يتغير العميل
      loadedCustomerRef.current = inv.customerId ?? 0;
      setForm(f => ({
        ...f,
        invoiceTypeId: inv.invoiceType,
        invoiceNumber: inv.invoiceNumber,
        issueDate: formatDateForInput(inv.issueDate),
        referenceNumber: inv.referenceNumber ?? inv.invoiceNumber,
        referenceDate: formatDateForInput(inv.referenceDate || inv.issueDate),
        customerId: inv.customerId ?? f.customerId,
        supplierId: inv.supplierId ?? 0,
        customerPrevBalance: parseFloat(inv.customerPrevBalance || 0),
        stockId: inv.stockId ?? f.stockId,
        toStockId: inv.toStockId ?? 0,
        salesmanId: inv.salesmanId ?? f.salesmanId,
        paymentMethodId: inv.paymentMethodId ?? f.paymentMethodId,
        safeId: inv.safeId ?? 0,
        bankId: inv.bankId ?? 0,
        notes: inv.notes || "",
        discount: parseFloat(0).toFixed(decimalPlaces), // خصم الفاتورة محفوظ داخل خصومات البنود — لا يُحمَّل مرتين
        invoiceStatus: inv.invoiceStatus ?? 0,
        paidAmount: parseFloat(inv.paidAmount || 0),
        journalEntryGlobalId: inv.journalEntryGlobalId ?? null,
        customHeaders: inv.customHeaders || {},
        lines,
      }));

      setShowApproveButton(inv.invoiceStatus === 0);
      setIsApproved(inv.invoiceStatus === 1);
      setCanUnapprove(!!inv.canUnapprove);
      setIsZatcaLocked(!!inv.isZatcaLocked);
      setZatcaStatus(inv.zatcaStatus || (inv.isSentToZatca ? "REPORTED" : null));
      toast.success("✅ تم إلغاء اعتماد الفاتورة وإرجاعها إلى مسودة");
    } catch (err) {
      toast.error(err?.response?.data || "❌ فشل إلغاء اعتماد الفاتورة");
    } finally {
      setIsSaving(false);
    }
  };

  const handleShowZatcaDetails = () => {
    // Here we could fetch details if not already loaded, 
    // or just show a message for now since we have the status.
    Swal.fire({
      title: 'حالة الربط مع الهيئة',
      text: zatcaStatus === "REPORTED" ? "تم إرسال الفاتورة بنجاح ✅" : "لم يتم الإرسال بعد أو يوجد خطأ",
      icon: zatcaStatus === "REPORTED" ? 'success' : 'info'
    });
  };

  function resetForm() {
    setEditingId(null);
    const newType = form.invoiceTypeId || initialType || 0;
    setForm({ ...initialFormState, invoiceTypeId: newType, invoiceStatus: defaultInvoiceStatus });
    fetchNextInvoiceNumber(newType);
    setShowApproveButton(false);
    setIsApproved(false);
    setCanUnapprove(false);
    setIsZatcaLocked(false);
    setZatcaStatus(null);
    applyProgramDefaults(newType);

    // Apply restricted values if present even without program defaults
    let forcedStockId = form.stockId;
    if (restrictedStockId) forcedStockId = restrictedStockId;
    else if (allowedStockIds.length > 0 && !allowedStockIds.includes(form.stockId)) forcedStockId = allowedStockIds[0];

    let forcedSafeId = form.safeId;
    if (restrictedSafeId) forcedSafeId = restrictedSafeId;
    else if (allowedSafeIds.length > 0 && !allowedSafeIds.includes(form.safeId)) forcedSafeId = allowedSafeIds[0];

    setForm(f => ({
        ...f,
        stockId: forcedStockId,
        safeId: forcedSafeId
    }));
    
    navigate(`/invoices/new/${newType}`, { replace: true });
  }

  // التنقل بين السجلات عبر API
  async function goToInvoice(action) {
    try {
      let res;
      const currentInvoiceId = editingId || id;

       if (action === 'last') {
        res = await invoiceApi.getLast(companyId, form.invoiceTypeId);

            } else if (action === 'previous') {
        if (currentInvoiceId) {
          res = await invoiceApi.getPrevious(currentInvoiceId);
        } else {
          // عند عدم وجود id في وضع فاتورة جديدة، اعتبر "السابق" هو الأخير
          res = await invoiceApi.getLast(companyId, form.invoiceTypeId);
        }
      } else if (action === 'next') {
        if (currentInvoiceId) {
          res = await invoiceApi.getNext(currentInvoiceId);
        } else {
          // عند عدم وجود id في وضع فاتورة جديدة، اعتبر "التالي" هو الأول
          res = await invoiceApi.getFirst(companyId, form.invoiceTypeId);
        }
      } else if (action === 'first') {
        res = await invoiceApi.getFirst(companyId, form.invoiceTypeId);

      }

      const inv = res?.data;
      const nextId = inv?.invGlobalID || inv?.id || inv?.InvGlobalID;
      if (nextId) {
        // تعبئة التفاصيل فورًا كما في وضع التعديل
        const rawItems = inv.items || inv.invoiceItems || inv.details || [];
        const lines = buildInvoiceLines(rawItems);

        // رصيد العميل المحفوظ في الفاتورة يبقى كما هو ما لم يتغير العميل
        loadedCustomerRef.current = inv.customerId ?? 0;
        setForm(f => ({
          ...f,
          invoiceTypeId: inv.invoiceType,
          invoiceNumber: inv.invoiceNumber,
          issueDate: formatDateForInput(inv.issueDate),
          referenceNumber: inv.referenceNumber ?? inv.invoiceNumber,
          referenceDate: formatDateForInput(inv.referenceDate || inv.issueDate),
          customerId: inv.customerId ?? f.customerId,
          supplierId: inv.supplierId ?? 0,
          customerPrevBalance: parseFloat(inv.customerPrevBalance || 0),
          stockId: inv.stockId ?? f.stockId,
          toStockId: inv.toStockId ?? 0,
          salesmanId: inv.salesmanId ?? f.salesmanId,
          paymentMethodId: inv.paymentMethodId ?? f.paymentMethodId,
          safeId: inv.safeId ?? 0,
          bankId: inv.bankId ?? 0,
          notes: inv.notes || "",
          discount: parseFloat(0).toFixed(decimalPlaces), // خصم الفاتورة محفوظ داخل خصومات البنود — لا يُحمَّل مرتين
          invoiceStatus: inv.invoiceStatus ?? 0,
          paidAmount: parseFloat(inv.paidAmount || 0),
          journalEntryGlobalId: inv.journalEntryGlobalId ?? null,
          customHeaders: inv.customHeaders || {},
          lines,
        }));

        setShowApproveButton(inv.invoiceStatus === 0);
        setIsApproved(inv.invoiceStatus === 1);
        setCanUnapprove(!!inv.canUnapprove);
        setIsZatcaLocked(!!inv.isZatcaLocked);

        setEditingId(nextId);
        navigate(`/invoices/edit/${nextId}`);
      } else {
        toast.error('لا توجد بيانات للتنقل');
      }
    } catch (err) {
      toast.error('حدث خطأ أثناء التنقل بين الفواتير');
    }
  }
  const getApprovePermission = (typeId) => {
    switch (typeId) {
      case 1: return PERMISSIONS.INVOICE_SALES_APPROVE;
      case 2: return PERMISSIONS.INVOICE_PURCHASE_APPROVE;
      case 3: return PERMISSIONS.INVOICE_SALES_RETURN_APPROVE;
      case 4: return PERMISSIONS.INVOICE_PURCHASE_RETURN_APPROVE;
      case 7: return PERMISSIONS.INVOICE_OPENING_STOCK_APPROVE;
      case 8: return PERMISSIONS.INVOICE_STOCK_IN_APPROVE;
      case 9: return PERMISSIONS.INVOICE_STOCK_OUT_APPROVE;
      case 12: return PERMISSIONS.STOCK_MOVE; // مناقلة
      case 13: return PERMISSIONS.STOCK_MOVE; // تسوية
      default: return null;
    }
  };

  const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
  const isCash = form.paymentMethodId === cashId;
  const isCredit = form.paymentMethodId === creditMethodId;
  const isNonCash = !isCash && !isCredit;

  const handleExportExcel = () => {
    const headerData = [
      [t('invoice.fields.invoiceType'), types.find(t => t.invoiceTypeId === form.invoiceTypeId)?.name || ""],
      [t('invoice.fields.invoiceNumber'), form.invoiceNumber],
      [t('invoice.fields.invoiceDate'), form.issueDate ? form.issueDate.slice(0, 10) : ""],
      [t('invoice.fields.customer'), customers.find(c => c.id === form.customerId)?.name || ""],
      [t('invoice.fields.notes'), form.notes],
      [], 
    ];

    const tableHeader = [
      "#",
      t('invoice.fields.item') || "الصنف",
      t('invoice.fields.description') || "الوصف",
      t('invoice.fields.unit') || "الوحدة",
      t('invoice.fields.quantity') || "الكمية",
      t('invoice.fields.price') || "السعر",
      t('invoice.fields.discount') || "الخصم",
      t('invoice.fields.taxPercent') || "الضريبة %",
      t('invoice.fields.total') || "المجموع",
      t('invoice.fields.net') || "الصافي"
    ];

    const itemsData = form.lines.map((ln, i) => {
        const qty = parseFloat(ln.qty) || 0;
        const price = parseFloat(ln.price) || 0;
        const discount = parseFloat(ln.discount) || 0;
        const taxRate = parseFloat(ln.taxRate) || 0;

        let lineTotalBeforeDiscount = qty * price;
        let lineAfterDiscount = lineTotalBeforeDiscount - discount;
        
        let taxable = lineAfterDiscount;
        let vat = 0;

        if (priceIncludesVAT) {
            taxable = lineAfterDiscount / (1 + taxRate);
            vat = lineAfterDiscount - taxable;
        } else {
            vat = lineAfterDiscount * taxRate;
        }
        
        const net = priceIncludesVAT ? lineAfterDiscount : lineAfterDiscount + vat;

        return [
            i + 1,
            ln.productName,
            ln.description,
            ln.unitName,
            qty,
            price,
            discount,
            (taxRate * 100).toFixed(2) + "%",
            lineTotalBeforeDiscount.toFixed(decimalPlaces),
            net.toFixed(decimalPlaces)
        ];
    });

    const footerData = [
        [],
        ["", "", "", "", "", "", "", t('invoice.summary.subtotal') || "المجموع الفرعي", totals.subtotal.toFixed(decimalPlaces)],
        ["", "", "", "", "", "", "", t('invoice.summary.invoiceDiscount') || "خصم الفاتورة", form.discount],
        ["", "", "", "", "", "", "", t('invoice.summary.vat') || "الضريبة", totals.totalVAT.toFixed(decimalPlaces)],
        ["", "", "", "", "", "", "", t('invoice.summary.net') || "الصافي", totals.net.toFixed(decimalPlaces)]
    ];

    const wsData = [...headerData, tableHeader, ...itemsData, ...footerData];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws['!cols'] = [
        { wch: 5 }, { wch: 30 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, 
        { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Invoice");
    XLSX.writeFile(wb, `Invoice_${form.invoiceNumber || 'New'}.xlsx`);
  };


  return (
  <div dir="rtl" style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'inherit', position: 'relative' }}>
    {isSaving && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(2px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
        <div style={{ width: '52px', height: '52px', border: '4px solid #dbeafe', borderTopColor: '#2563eb', borderRadius: '9999px', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ color: '#1e3a8a', fontWeight: '800', fontSize: '15px' }}>جاري حفظ الفاتورة، يرجى الانتظار...</div>
      </div>
    )}

    {/* ══ HEADER ══ */}
    <div style={{ background: 'linear-gradient(to left, #1d4ed8, #2563eb)', padding: isMobile ? '12px 14px' : '0 20px', minHeight: isMobile ? 'auto' : '56px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? '10px' : '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>

      {/* Right: Title */}
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: isMobile ? 'space-between' : 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ color: 'white', fontSize: isMobile ? '16px' : '18px', fontWeight: '800', margin: 0, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
          {mode === "view" ? t('invoice.titles.viewInvoice')
            : mode === "edit" ? t('invoice.titles.editInvoice')
            : mode === "return" ? t('invoice.titles.returnInvoice')
            : mode === "copy" ? t('invoice.titles.copyInvoice', { defaultValue: 'نسخة فاتورة' })
            : t('invoice.titles.newInvoice')}
        </h1>
        {zatcaStatus === "REPORTED" && (
          <span onClick={handleShowZatcaDetails} style={{ background: '#10b981', color: 'white', fontSize: '11px', padding: '2px 8px', borderRadius: '999px', fontWeight: '700', cursor: 'pointer' }}>✅ ZATCA</span>
        )}
        {zatcaStatus === "FAILED" && (
          <span onClick={handleShowZatcaDetails} style={{ background: '#ef4444', color: 'white', fontSize: '11px', padding: '2px 8px', borderRadius: '999px', fontWeight: '700', cursor: 'pointer' }}>❌ ZATCA</span>
        )}
      </div>

      {/* Center: Type + Number */}
      <div style={{ background: 'white', borderRadius: '12px', padding: isMobile ? '8px 12px' : '6px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? '12px' : '16px', alignSelf: isMobile ? 'stretch' : 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('invoice.fields.invoiceType')}</div>
          <div style={{ fontSize: '14px', fontWeight: '800', color: '#2563eb' }}>{types.find(t => t.invoiceTypeId === form.invoiceTypeId)?.name || '—'}</div>
        </div>
        <div style={{ width: '1px', height: '32px', background: '#e2e8f0' }} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('invoice.fields.invoiceNumber')}</div>
          <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '900', color: '#2563eb', lineHeight: 1 }}>{form.invoiceNumber || '—'}</div>
        </div>
      </div>

      {vatExempt ? (
        <span title="هذا النوع من الفواتير بدون ضريبة (من إعدادات البرنامج)"
          style={{ alignSelf: isMobile ? 'stretch' : 'auto', textAlign: 'center', height: '36px', lineHeight: '34px', padding: '0 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 800, background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}>
          بدون ضريبة
        </span>
      ) : (
      <Button type="button" onClick={togglePriceIncludesVAT} title={t('programSettings.priceIncludesVAT')} className={(priceIncludesVAT ? "bg-blue-100 text-green-700 border-green-300" : "bg-amber-100 text-amber-700 border-amber-300") + " h-9 px-3 rounded-md text-xs font-bold border"} style={{ alignSelf: isMobile ? 'stretch' : 'auto', width: isMobile ? '100%' : 'auto' }}>
  {priceIncludesVAT ? t('programSettings.priceIncludesVAT') : t('pos.policy.priceExcludesVAT')}
    </Button>
      )}

      {/* Left: Nav buttons */}
      <div style={{ display: 'flex', gap: '6px', alignSelf: isMobile ? 'stretch' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
        {editingId && (
          <button type="button" title={t('invoice.buttons.copyInvoice', { defaultValue: 'نسخ الفاتورة' })}
            onClick={() => window.open(`/invoices/new/${form.invoiceTypeId}?copyOf=${editingId}`, "_blank")}
            style={{ height: '36px', padding: '0 12px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '13px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            ⧉ {t('invoice.buttons.copy', { defaultValue: 'نسخ' })}
          </button>
        )}
        {[['last','⏭'],['next','▶'],['previous','◀'],['first','⏮']].map(([action, icon]) => (
          <button key={action} type="button" onClick={() => goToInvoice(action)}
            style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            {icon}
          </button>
        ))}
      </div>
    </div>

    {/* ══ BODY ══ */}
    <div style={{ padding: isMobile ? '12px' : '16px 20px' }}>
      <form onSubmit={handleSubmit}>
        {mode === "copy" && (
          <div style={{ marginBottom: '12px', background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '12px 14px', fontSize: '14px', fontWeight: '700' }}>
            {copySourceNumber
              ? `هذه فاتورة جديدة منسوخة من الفاتورة رقم ${copySourceNumber} — راجع البيانات ثم احفظ.`
              : 'جاري تحميل بيانات الفاتورة المنسوخة...'}
          </div>
        )}
        {editingId && isApproved && (
          <div style={{ marginBottom: '12px', background: isZatcaLocked ? '#fef2f2' : '#eff6ff', color: isZatcaLocked ? '#991b1b' : '#1d4ed8', border: `1px solid ${isZatcaLocked ? '#fecaca' : '#bfdbfe'}`, borderRadius: '12px', padding: '12px 14px', fontSize: '14px', fontWeight: '700' }}>
            {isZatcaLocked
              ? 'هذه الفاتورة معتمدة وربط زاتكا مفعل، لذلك لا يمكن تعديلها أو إلغاء اعتمادها.'
              : 'هذه الفاتورة معتمدة. لإجراء التعديل، قم أولًا بإلغاء الاعتماد لإرجاعها إلى مسودة.'}
          </div>
        )}

        {/* ─ Section 1: Header Fields ─ */}
        <div style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '14px' : '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>

          {/* Row 1: Main fields */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(160px, 1fr))', gap: isMobile ? '12px' : '16px' }}>

            {/* Date */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.invoiceDate')}</label>
              <input type="date" name="issueDate" value={form.issueDate} onChange={handleChange} readOnly={viewMode}
                style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
            </div>

            {/* Warehouse */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>
                {form.invoiceTypeId === 12 ? (t('invoice.fields.fromWarehouse') || 'من مستودع') : t('invoice.fields.warehouse')}
              </label>
              <select name="stockId" value={form.stockId} onChange={handleChange}
                disabled={viewMode || !!restrictedStockId}
                style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                <option value={0}>{t('invoice.options.selectWarehouse')}</option>
                {stocks.filter(s => {
                  if (restrictedStockId) return s.id === restrictedStockId;
                  if (allowedStockIds.length > 0) return allowedStockIds.includes(s.id);
                  return true;
                }).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {/* To Warehouse */}
            {form.invoiceTypeId === 12 && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.toWarehouse') || 'إلى مستودع'}</label>
                <select name="toStockId" value={form.toStockId} onChange={handleChange} disabled={viewMode}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                  <option value={0}>{t('invoice.options.selectToWarehouse')}</option>
                  {stocks.filter(s => Number(s.id) !== Number(form.stockId)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            {/* Payment Method */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.paymentMethod')}</label>
              <select name="paymentMethodId" value={form.paymentMethodId} onChange={handleChange} disabled={viewMode}
                style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                <option value={0}>{t('invoice.options.selectPaymentMethod')}</option>
                {methods.map(m => <option key={m.id} value={m.id}>{m.nameAr}</option>)}
              </select>
            </div>

            {/* Safe */}
            {isCash && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.safe')}</label>
                <select name="safeId" value={form.safeId} onChange={handleChange}
                  disabled={viewMode || !!restrictedSafeId}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                  <option value={0}>{t('invoice.options.selectSafe')}</option>
                  {safes.filter(s => {
                    if (restrictedSafeId) return s.safeId === restrictedSafeId;
                    if (allowedSafeIds.length > 0) return allowedSafeIds.includes(s.safeId);
                    return true;
                  }).map(s => <option key={s.safeId} value={s.safeId}>{s.name}</option>)}
                </select>
              </div>
            )}

            {/* Bank */}
            {isNonCash && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.bank')}</label>
                <select name="bankId" value={form.bankId} onChange={handleChange} disabled={viewMode}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                  <option value={0}>{t('invoice.options.selectBank')}</option>
                  {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}

            {/* Status */}
            {visibleFields.InvoiceStatus && (
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.invoiceStatus')}</label>
              <select name="invoiceStatus" value={form.invoiceStatus} onChange={handleChange} disabled={viewMode}
                style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                <option value={0}>{t('invoice.options.draft')}</option>
                <option value={1}>{t('invoice.options.approved')}</option>
                <option value={2}>{t('invoice.options.paid')}</option>
              </select>
            </div>
            )}

          </div>

          {/* Row 2: Customer (full width row) */}
          {isCustomerParty && (
  <div style={{
    marginTop: '16px',
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : ['1fr', visibleFields.Notes && '1fr', form.customerId !== 0 && visibleFields.CustomerPrevBalance && 'auto'].filter(Boolean).join(' '),
    gap: isMobile ? '12px' : '16px',
    alignItems: 'end'
  }}>
    {/* العميل */}
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.customer')}</label>
      <div style={{ display: 'flex', gap: '8px', flexDirection: isMobile ? 'column' : 'row' }}>
        <Autocomplete
          options={customers}
          getOptionLabel={(o) => o.name || ""}
          value={customers.find(c => c.id === form.customerId) || null}
          onChange={(e, v) => handleChange({ target: { name: 'customerId', value: v ? v.id : 0 } })}
          disabled={viewMode}
          style={{ flex: 1, width: '100%' }}
          size="small"
          renderInput={(params) => (
            <TextField {...params} placeholder={t('invoice.options.selectCustomer')} size="small"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#f8fafc", borderRadius: "10px", fontWeight: "700", fontSize: "14px", height: "42px", "& fieldset": { borderWidth: "2px", borderColor: "#e2e8f0" } } }} />
          )}
        />
        {!viewMode && (
          <button type="button" onClick={() => setOpenCustomerModal(true)}
            style={{ width: isMobile ? '100%' : '42px', height: '42px', background: '#2563eb', border: 'none', borderRadius: '10px', color: 'white', fontSize: '20px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            +
          </button>
        )}
      </div>
    </div>

    {/* الملاحظات - أصبحت بنفس الصف */}
    {visibleFields.Notes && (
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.notes')}</label>
      <input name="notes" value={form.notes} onChange={handleChange} readOnly={viewMode}
        placeholder="اكتب ملاحظاتك هنا..."
        style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '600', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
    </div>
    )}

    {/* رصيد العميل السابق */}
    {form.customerId !== 0 && visibleFields.CustomerPrevBalance && (
      <div style={{ minWidth: isMobile ? '100%' : '160px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.customerPrevBalance')}</label>
        {(() => {
          const prev = Number(form.customerPrevBalance) || 0;
          // موجب = على العميل (مدين)، سالب = له (دائن)
          const tone = prev > 0 ? { bg: '#fef2f2', bd: '#fecaca', fg: '#dc2626', tag: 'عليه' }
            : prev < 0 ? { bg: '#ecfdf5', bd: '#a7f3d0', fg: '#047857', tag: 'له' }
            : { bg: '#f8fafc', bd: '#e2e8f0', fg: '#64748b', tag: '' };
          // الرصيد بعد الفاتورة: المبيعات الآجلة تزيد ما على العميل بالمتبقي (الصافي − المدفوع)
          const remaining = (Number(totals.net) || 0) - (Number(form.paidAmount) || 0);
          const after = isOutbound && form.invoiceTypeId !== 10 ? prev + remaining : null;
          const fmt = (v) => Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces });
          return (
            <>
              <div style={{ height: '42px', background: tone.bg, border: `2px solid ${tone.bd}`, borderRadius: '10px', padding: '0 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: '800', color: tone.fg, fontSize: '15px', direction: 'ltr' }}>{fmt(prev)}</span>
                {tone.tag && <span style={{ fontSize: '11.5px', fontWeight: 800, color: tone.fg, background: '#fff', borderRadius: 999, padding: '1px 8px' }}>{tone.tag}</span>}
              </div>
              {after !== null && Math.abs(remaining) > 0.0001 && (
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: 4, fontWeight: 700 }}>
                  بعد الفاتورة: <span style={{ direction: 'ltr', unicodeBidi: 'embed', color: after > 0 ? '#dc2626' : after < 0 ? '#047857' : '#64748b' }}>{fmt(after)}</span>
                  {after !== 0 && <span> ({after > 0 ? 'عليه' : 'له'})</span>}
                </div>
              )}
            </>
          );
        })()}
      </div>
    )}
  </div>
)}

          {/* Row 2-المورد (كامل مع Autocomplete مثل العميل) */}
          {isSupplierParty && (
  <div style={{
    marginTop: '16px',
    display: 'grid',
    gridTemplateColumns: isMobile || !visibleFields.Notes ? '1fr' : '1fr 1fr',
    gap: isMobile ? '12px' : '16px',
    alignItems: 'end'
  }}>
    {/* المورد */}
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#16a34a', marginBottom: '6px' }}>{t('invoice.fields.supplier')}</label>
      <div style={{ display: 'flex', gap: '8px', flexDirection: isMobile ? 'column' : 'row' }}>
        <Autocomplete
          options={suppliers}
          getOptionLabel={(o) => o.name || ""}
          value={suppliers.find(s => s.id === form.supplierId) || null}
          onChange={(e, v) => handleChange({ target: { name: 'supplierId', value: v ? v.id : 0 } })}
          disabled={viewMode}
          style={{ flex: 1, width: '100%' }}
          size="small"
          renderInput={(params) => (
            <TextField {...params} placeholder={t('invoice.options.selectSupplier')} size="small"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#f0fdf4", borderRadius: "10px", fontWeight: "700", fontSize: "14px", height: "42px", "& fieldset": { borderWidth: "2px", borderColor: "#86efac" } } }} />
          )}
        />
        {!viewMode && (
          <button type="button" onClick={() => setOpenSupplierModal(true)}
            style={{ width: isMobile ? '100%' : '42px', height: '42px', background: '#16a34a', border: 'none', borderRadius: '10px', color: 'white', fontSize: '20px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            +
          </button>
        )}
      </div>
    </div>

    {/* الملاحظات - بنفس الصف مع المورد */}
    {visibleFields.Notes && (
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.notes')}</label>
      <input name="notes" value={form.notes} onChange={handleChange} readOnly={viewMode}
        placeholder="اكتب ملاحظاتك هنا..."
        style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '600', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
    </div>
    )}
  </div>
)}

{/* ✅ الملاحظات فقط عندما لا يوجد طرف (لا عميل ولا مورد) */}
{!isCustomerParty && !isSupplierParty && visibleFields.Notes && (
  <div style={{ marginTop: '16px' }}>
    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.notes')}</label>
    <input name="notes" value={form.notes} onChange={handleChange} readOnly={viewMode}
      placeholder="اكتب ملاحظاتك هنا..."
      style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '600', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
  </div>
)}

          

          {/* Reference */}
          {isInbound && visibleFields.Reference && (
            <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '12px' : '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.referenceNumber')}</label>
                <input name="referenceNumber" value={form.referenceNumber} onChange={handleChange} readOnly={viewMode}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.referenceDate')}</label>
                <input name="referenceDate" type="date" value={form.referenceDate} onChange={handleChange} readOnly={viewMode}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
              </div>
            </div>
          )}

          {/* الحقول المخصصة لنوع الفاتورة */}
          {customFields.length > 0 && (
            <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: isMobile ? '12px' : '16px' }}>
              {customFields.map(cf => {
                const value = form.customHeaders?.[cf.fieldName] ?? '';
                const inputStyle = { width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' };
                const label = (
                  <label htmlFor={`cf-${cf.fieldName}`} style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>
                    {cf.displayName}{cf.isRequired && <span style={{ color: '#e11d48', marginRight: 4 }}>*</span>}
                  </label>
                );
                if (cf.dataType === 'bool') {
                  return (
                    <div key={cf.fieldName}>
                      {label}
                      <label style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: 10, cursor: viewMode ? 'default' : 'pointer' }}>
                        <input id={`cf-${cf.fieldName}`} type="checkbox" checked={value === 'true'} disabled={viewMode}
                          onChange={e => setCustomValue(cf.fieldName, e.target.checked ? 'true' : 'false')}
                          style={{ width: 18, height: 18, accentColor: '#4f46e5' }} />
                        <span style={{ fontWeight: 700, color: value === 'true' ? '#4f46e5' : '#94a3b8' }}>{value === 'true' ? 'نعم' : 'لا'}</span>
                      </label>
                    </div>
                  );
                }
                return (
                  <div key={cf.fieldName}>
                    {label}
                    {cf.dataType === 'list' ? (
                      <select id={`cf-${cf.fieldName}`} value={value} disabled={viewMode}
                        onChange={e => setCustomValue(cf.fieldName, e.target.value)} style={inputStyle}>
                        <option value="">—</option>
                        {(cf.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input id={`cf-${cf.fieldName}`} value={value} readOnly={viewMode}
                        type={cf.dataType === 'date' ? 'date' : 'text'}
                        inputMode={cf.dataType === 'decimal' ? 'decimal' : undefined}
                        onChange={e => setCustomValue(cf.fieldName, cf.dataType === 'decimal'
                          ? e.target.value.replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660)).replace(/[^0-9.-]/g, '')
                          : e.target.value)}
                        style={{ ...inputStyle, direction: cf.dataType === 'string' ? undefined : 'ltr', textAlign: cf.dataType === 'string' ? undefined : 'right' }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ─ Section 2: Items Toolbar ─ */}
        
{!viewMode && (
  <Box
    sx={{
      bgcolor: "background.paper",
      borderRadius: 3,
      p: isMobile ? 1.5 : 2,
      mb: 2,
      boxShadow: 1,
      border: 1,
      borderColor: "divider",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 1.25,
    }}
  >
    {/* إضافة سطر صنف */}
    <Button
      type="button"
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      onClick={handleAddLine}
      sx={{
        height: 42,
        borderRadius: 2,
        fontWeight: 700,
        width: isMobile ? "100%" : "auto",
        whiteSpace: "nowrap",
      }}
    >
      {t("invoice.buttons.addItemLine")}
    </Button>

    {/* الإدراج المتعدد */}
    <Button
      type="button"
      variant="outlined"
      color="secondary"
      startIcon={<AddIcon />}
      onClick={() => setBulkOpen(true)}
      sx={{
        height: 42,
        borderRadius: 2,
        fontWeight: 700,
        px: 2,
        width: isMobile ? "100%" : "auto",
        whiteSpace: "nowrap",
      }}
    >
      {t("invoice.buttons.insertmulti")}
    </Button>

    {/* استيراد الأصناف */}
    <Button
      type="button"
      variant="outlined"
      color="success"
      startIcon={<AddIcon />}
      onClick={() => setImportOpen(true)}
      sx={{
        height: 42,
        borderRadius: 2,
        fontWeight: 700,
        px: 2,
        width: isMobile ? "100%" : "auto",
        whiteSpace: "nowrap",
      }}
    >
      {t("invoice.buttons.ImportInv")}
    </Button>

    {/* إضافة منتج جديد */}
    <Button
      type="button"
      variant="contained"
      color="info"
      startIcon={<AddIcon />}
      onClick={() => navigate("/add-product")}
      sx={{
        height: 42,
        borderRadius: 2,
        fontWeight: 700,
        width: isMobile ? "100%" : "auto",
        whiteSpace: "nowrap",
      }}
    >
      إضافة منتج
    </Button>

    {/* الباركود */}
    <TextField
      size="small"
      fullWidth={isMobile}
      placeholder={t("scanBarcode") || "🔍 مسح الباركود..."}
      value={barcode}
      onChange={(e) => setBarcode(e.target.value)}
      onKeyDown={handleBarcodeKeyDown}
      onBlur={(e) => {
        if (!barcodeLoading && e.target.value.trim()) {
          insertProductByBarcode(e.target.value);
        }
      }}
      disabled={barcodeLoading}
      autoFocus
      sx={{
        flex: 1,
        minWidth: isMobile ? "100%" : 200,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          bgcolor: "background.default",
        },
      }}
    />
  </Box>
)}


        {/* ─ Section 3: Items Table ─ */}
        <div style={{ background: 'white', borderRadius: '16px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: isMobile ? '980px' : '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#1e293b', color: 'white' }}>
                  <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap', width: '40px' }}>#</th>
                  {visibleColumns.map(k => {
                    const w = itemWidths[k];
                    return (
                      <th key={k} style={{
                        padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap',
                        width: w ? `${w}px` : undefined, minWidth: w ? `${w}px` : (k === 'ProductId' ? '180px' : '120px'),
                        background: k === 'Net' ? '#1d4ed8' : undefined,
                      }}>
                        {itemLabels[k]}
                      </th>
                    );
                  })}
                  {!viewMode                     && <th style={{ padding: '14px 8px', width: '44px' }}></th>}
                </tr>
              </thead>
              <tbody>
                {form.lines.map((ln, i) => {
                  const qty = parseFloat(ln.qty) || 0;
                  const price = parseFloat(ln.price) || 0;
                  const discount = parseFloat(ln.discount) || 0;
                  const taxRate = parseFloat(ln.taxRate) || 0;
                  const lineTotal = qty * price;
                  const promoDisc = parseFloat(ln.promoDiscount) || 0;
                  const afterDisc = lineTotal - discount - promoDisc;
                  const vat = priceIncludesVAT ? afterDisc - afterDisc / (1 + taxRate) : afterDisc * taxRate;
                  const lineNet = priceIncludesVAT ? afterDisc : afterDisc + vat;
                  const rowBg = i % 2 === 0 ? '#ffffff' : '#f8fafc';

                  return (
                    <tr key={i} style={{ background: rowBg, borderBottom: '1px solid #f1f5f9' }}
                      onMouseOver={e => e.currentTarget.style.background = '#eff6ff'}
                      onMouseOut={e => e.currentTarget.style.background = rowBg}>

                      <td style={{ padding: '10px 12px', textAlign: 'center', color: '#94a3b8', fontWeight: '700', fontSize: '13px' }}>{i + 1}</td>

                      {visibleColumns.map(k => {
                        switch (k) {
                          case 'ProductId': return (
                        <td key={k} style={{ padding: '8px' }}>
                          <input type="text" list={`product-options-${i}`}
                            data-line-field={`productName-${i}`}
                            value={ln.productName} disabled={viewMode}
                            onKeyDown={(e) => handleProductKeyDown(e, i)}
                            onFocus={(e) => e.target.select()}
                            autoComplete="off"
                            onChange={(e) => { handleLineChange(i, "productName", e.target.value); setProductSearch(e.target.value); }}
                            placeholder={t('invoice.placeholders.searchItem')}
                            style={{ width: '100%', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 10px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', boxSizing: 'border-box' }} />
                          {promoDisc > 0 && (
                            <div title={ln.promoName} style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', padding: '1px 8px', borderRadius: 999, background: '#fdf2f8', color: '#be185d', fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              🏷️ {ln.promoName} <span style={{ direction: 'ltr' }}>−{promoDisc.toFixed(decimalPlaces)}</span>
                            </div>
                          )}
                          {ln.promoHint && !viewMode && (
                            <button type="button" title={`${ln.promoHint.name} — اضغط لإضافة الكمية المجانية`}
                              onClick={() => handleLineChange(i, "qty", ((parseFloat(ln.qty) || 0) + ln.promoHint.qty).toFixed(decimalPlaces))}
                              style={{ marginTop: 4, marginInlineStart: 4, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', borderRadius: 999, border: '1px dashed #f59e0b', background: '#fffbeb', color: '#b45309', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              🎁 أضف {ln.promoHint.qty} مجانًا
                            </button>
                          )}
                          <datalist id={`product-options-${i}`}>
                            {pickableProducts.filter(p => (p.arabicName || "").toLowerCase().includes((productSearch || "").toLowerCase()))
                              .map(p => <option key={p.id} value={p.arabicName} />)}
                          </datalist>
                        </td>
                          );
                          case 'Description': return (
                        <td key={k} style={{ padding: '8px' }}>
                          <input type="text" data-line-field={`description-${i}`} value={ln.description ?? ''} disabled={viewMode}
                            onChange={(e) => handleLineChange(i, "description", e.target.value)}
                            onKeyDown={(e) => handleLineFieldKeyDown(e, i, "description")}
                            placeholder={t('invoice.placeholders.description')}
                            style={{ width: '100%', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 10px', fontSize: '14px', fontWeight: '600', background: 'white', outline: 'none', boxSizing: 'border-box' }} />
                        </td>
                          );
                          case 'Unit': return (
                        <td key={k} style={{ padding: '8px' }}>
                          <select data-line-field={`unitId-${i}`} value={ln.unitId} disabled={viewMode}
                            onChange={(e) => handleLineChange(i, "unitId", e.target.value)}
                            onKeyDown={(e) => handleLineFieldKeyDown(e, i, "unitId")}
                            style={{ width: '100%', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', boxSizing: 'border-box' }}>
                            <option value={0}>—</option>
                            {ln.unitsList.map(u => <option key={u.unitId} value={u.unitId}>{u.unitNameAr}</option>)}
                          </select>
                        </td>
                          );
                          case 'Quantity': return (
                        <td key={k} style={{ padding: '8px' }}>
                          <input type="text" inputMode="decimal" data-line-field={`qty-${i}`} value={ln.qty} disabled={viewMode}
                            onChange={(e) => { const v = e.target.value; if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v)) handleLineChange(i, "qty", v); }}
                            onBlur={(e) => handleLineChange(i, "qty", (parseFloat(e.target.value) || 0).toFixed(decimalPlaces))}
                            onKeyDown={(e) => handleLineFieldKeyDown(e, i, "qty")}
                            onFocus={(e) => e.target.select()}
                            style={{ width: '100%', minWidth: '60px', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }} />
                        </td>
                          );
                          case 'UnitPrice': return (
                        <td key={k} style={{ padding: '8px' }}>
                          <input type="text" inputMode="decimal" data-line-field={`price-${i}`} value={ln.price} disabled={viewMode}
                            onChange={(e) => { const v = e.target.value; if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v)) handleLineChange(i, "price", v); }}
                            onBlur={(e) => handleLineChange(i, "price", (parseFloat(e.target.value) || 0).toFixed(decimalPlaces))}
                            onKeyDown={(e) => handleLineFieldKeyDown(e, i, "price")}
                            onFocus={(e) => e.target.select()}
                            style={{ width: '100%', minWidth: '60px', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }} />
                        </td>
                          );
                          case 'LineTotal': return (
                        <td key={k} style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '14px', background: '#f9fafb', whiteSpace: 'nowrap' }}>
                          {lineTotal.toFixed(decimalPlaces)}
                        </td>
                          );
                          case 'Discount': return (
                        <td key={k} style={{ padding: '8px' }}>
                          <input type="text" inputMode="decimal" data-line-field={`discount-${i}`} value={ln.discount} disabled={viewMode}
                            onChange={(e) => { const v = e.target.value; if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v)) handleLineChange(i, "discount", v); }}
                            onBlur={(e) => handleLineChange(i, "discount", (parseFloat(e.target.value) || 0).toFixed(decimalPlaces))}
                            onKeyDown={(e) => handleLineFieldKeyDown(e, i, "discount")}
                            onFocus={(e) => e.target.select()}
                            style={{ width: '100%', minWidth: '60px', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }} />
                        </td>
                          );
                          case 'TaxPercent': return (
                        <td key={k} style={{ padding: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                            <input type="number" step={stepValue} readOnly={viewMode || vatExempt}
                              title={vatExempt ? "بدون ضريبة" : undefined}
                              value={(parseFloat(ln.taxRate) * 100).toFixed(decimalPlaces)}
                              onChange={(e) => handleLineChange(i, "taxRate", parseFloat(e.target.value) / 100)}
                              style={{ width: '60px', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 6px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }} />
                            <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '700' }}>%</span>
                          </div>
                        </td>
                          );
                          case 'Net': return (
                        <td key={k} style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '800', color: '#1d4ed8', fontSize: '15px', background: '#eff6ff', whiteSpace: 'nowrap' }}>
                          {lineNet.toFixed(decimalPlaces)}
                        </td>
                          );
                          default: return null;
                        }
                      })}

                      {!viewMode && (
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <button type="button" onClick={() => handleRemoveLine(i)}
                            style={{ width: '32px', height: '32px', background: '#fef2f2', border: 'none', borderRadius: '8px', color: '#ef4444', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', opacity: 0.7 }}
                            onMouseOver={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.background = '#fee2e2'; }}
                            onMouseOut={e => { e.currentTarget.style.opacity = 0.7; e.currentTarget.style.background = '#fef2f2'; }}>
                            ✕
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}

                {form.lines.length === 0 && (
                  <tr>
                    <td colSpan={1 + visibleColumns.length + (!viewMode ? 1 : 0)} style={{ padding: '60px 20px', textAlign: 'center' }}>
                      <div style={{ color: '#cbd5e1', fontSize: '48px', marginBottom: '12px' }}>📋</div>
                      <div style={{ color: '#94a3b8', fontWeight: '700', fontSize: '15px', marginBottom: '12px' }}>{t('invoice.messages.noLinesYet')}</div>
                      {!viewMode && (
                        <button type="button" onClick={handleAddLine}
                          style={{ color: '#2563eb', background: 'none', border: 'none', fontWeight: '700', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}>
                          + {t('invoice.buttons.addItemLine')}
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ─ Section 4: Footer ─ */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>

          {/* Action Buttons */}
          
<div
  style={{
    flex: 1,
    width: isMobile ? '100%' : 'auto',
    background: 'white',
    borderRadius: '16px',
    padding: isMobile ? '12px' : '16px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    minWidth: isMobile ? '100%' : '300px',
    boxSizing: 'border-box',
  }}
>
  {/* جديد */}
  {!viewMode && (
    <Button
      type="button"
      variant="outlined"
      color="secondary"
      startIcon={<span>🧾</span>}
      onClick={resetForm}
      disabled={isSaving}
      sx={{
        minHeight: '42px',
        px: 2,
        borderRadius: 2,
        fontSize: '14px',
        fontWeight: 'bold',
        width: isMobile ? '100%' : 'auto',
      }}
    >
      {t('invoice.buttons.new')}
    </Button>
  )}

  {/* حفظ الفاتورة */}
  {!viewMode && !isApproved && (
    <Button
      type="submit"
      variant="contained"
      color="success"
      disabled={isSaving}
      startIcon={<span>💾</span>}
      sx={{
        minHeight: '42px',
        px: 2.5,
        borderRadius: 2,
        fontSize: '14px',
        fontWeight: 'bold',
        boxShadow: 2,
        '&:hover': {
          boxShadow: 4,
        },
        width: isMobile ? '100%' : 'auto',
      }}
    >
      {t('invoice.buttons.saveInvoice')}
    </Button>
  )}

  {/* طباعة */}
  {id && (
    <Button
      type="button"
      variant="outlined"
      color="primary"
      startIcon={<span>🖨️</span>}
      onClick={() =>
        window.open(`/invoices/print/${id}`, '_blank')
      }
      sx={{
        minHeight: '42px',
        px: 2,
        borderRadius: 2,
        fontSize: '14px',
        fontWeight: 'bold',
        width: isMobile ? '100%' : 'auto',
      }}
    >
      طباعة
    </Button>
  )}

  {/* Excel */}
  <Button
    type="button"
    variant="outlined"
    color="success"
    startIcon={<span>📊</span>}
    onClick={handleExportExcel}
    sx={{
      minHeight: '42px',
      px: 2,
      borderRadius: 2,
      fontSize: '14px',
      fontWeight: 'bold',
      width: isMobile ? '100%' : 'auto',
    }}
  >
    Excel
  </Button>

  {/* عرض القيد */}
  {form.journalEntryGlobalId && (
    <Button
      type="button"
      variant="outlined"
      color="info"
      startIcon={<span>👁️</span>}
      onClick={() =>
        window.open(
          `/journal-entries/${form.journalEntryGlobalId}/view`,
          '_blank'
        )
      }
      sx={{
        minHeight: '42px',
        px: 2,
        borderRadius: 2,
        fontSize: '14px',
        fontWeight: 'bold',
        width: isMobile ? '100%' : 'auto',
      }}
    >
      القيد
    </Button>
  )}

  {/* اعتماد الفاتورة */}
  {editingId &&
    showApproveButton &&
    !isApproved &&
    hasPermission(getApprovePermission(form.invoiceTypeId)) && (
      <Button
        type="button"
        variant="contained"
        color="primary"
        startIcon={<span>✅</span>}
        onClick={handleApprove}
        disabled={isSaving}
        sx={{
          minHeight: '42px',
          px: 2.5,
          borderRadius: 2,
          fontSize: '14px',
          fontWeight: 'bold',
          boxShadow: 2,
          '&:hover': {
            boxShadow: 4,
          },
          width: isMobile ? '100%' : 'auto',
        }}
      >
        {t('invoice.buttons.approveInvoice')}
      </Button>
    )}

  {/* إلغاء الاعتماد */}
  {editingId && isApproved && canUnapprove && (
    <Button
      type="button"
      variant="outlined"
      color="warning"
      startIcon={<span>↩️</span>}
      onClick={handleUnapprove}
      disabled={isSaving}
      sx={{
        minHeight: '42px',
        px: 2.5,
        borderRadius: 2,
        fontSize: '14px',
        fontWeight: 'bold',
        width: isMobile ? '100%' : 'auto',
      }}
    >
      إلغاء الاعتماد
    </Button>
  )}

  {/* تخصيص المظهر */}
  <Button
    type="button"
    variant="outlined"
    color="secondary"
    startIcon={<span>⚙️</span>}
    onClick={() => setLayoutOpen(true)}
    sx={{
      minHeight: '42px',
      px: 2,
      borderRadius: 2,
      fontSize: '14px',
      fontWeight: 'bold',
      width: isMobile ? '100%' : 'auto',
    }}
  >
    {t('invoice.buttons.customizeAppearance')}
  </Button>
</div>


          {/* Totals Panel */}
          
{/* حقل إدخال الخصم خارج صندوق الإجماليات */}
<div
  style={{
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '8px',
  }}
>
  <div
    style={{
      width: isMobile ? '100%' : '300px',
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      border: '1px solid #e2e8f0',
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      boxSizing: 'border-box',
    }}
  >
    <label
      htmlFor="invoice-discount"
      style={{
        fontSize: '13px',
        fontWeight: '700',
        color: '#64748b',
        whiteSpace: 'nowrap',
      }}
    >
      الخصم
    </label>

    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginInlineStart: 'auto' }}>
      <div style={{ position: 'relative' }}>
        <input
          id="invoice-discount-pct"
          type="text"
          inputMode="decimal"
          value={docDiscountPctShown}
          readOnly={viewMode}
          placeholder="0"
          title="نسبة الخصم من إجمالي الفاتورة (بعد خصومات السطور)"
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const v = e.target.value.replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660)).replace('%', '');
            if (v === '') { setDocDiscountPct(null); setForm(f => ({ ...f, discount: (0).toFixed(decimalPlaces) })); return; }
            if (/^[0-9]*\.?[0-9]*$/.test(v) && parseFloat(v || 0) <= 100) setDocDiscountPct(v);
          }}
          style={{
            width: '70px', height: '36px', boxSizing: 'border-box',
            border: `2px solid ${docDiscountPct !== null ? '#fdba74' : '#e2e8f0'}`, borderRadius: '8px',
            padding: '0 18px 0 6px', fontSize: '14px', fontWeight: '700', textAlign: 'center',
            background: docDiscountPct !== null ? '#fff7ed' : '#f8fafc', outline: 'none',
          }}
        />
        <span style={{ position: 'absolute', right: '7px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', fontWeight: '800', color: '#94a3b8', pointerEvents: 'none' }}>%</span>
      </div>

    <input
  id="invoice-discount"
  type="text"
  inputMode="decimal"
  name="discount"
  value={form.discount ?? ''}
  onChange={(e) => { setDocDiscountPct(null); handleChange(e); }}
  onFocus={(e) => e.target.select()}
  readOnly={viewMode}
  placeholder="0.00"
  style={{
    width: '110px',
    maxWidth: '50%',
    height: '36px',
    boxSizing: 'border-box',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    padding: '0 10px',
    fontSize: '14px',
    fontWeight: '700',
    textAlign: 'center',
    background: '#f8fafc',
    outline: 'none',
  }}
/>
    </div>
  </div>
</div>

{/* صندوق الإجماليات */}

<div
  style={{
    width: isMobile ? '100%' : '300px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    flexShrink: 0,
  }}
>
  {[
    {
      label: t('invoice.summary.subtotal'),
      value: totals.subtotal.toFixed(decimalPlaces),
    },
    {
      label: 'إجمالي الخصم',
      value: totals.totalDiscount.toFixed(decimalPlaces),
    },
    ...(totals.totalPromo > 0 ? [{
      label: '🏷️ منها خصم العروض',
      value: totals.totalPromo.toFixed(decimalPlaces),
    }] : []),
    {
      label: t('invoice.summary.afterDiscount'),
      value: totals.totalAfterDiscount.toFixed(decimalPlaces),
    },
    {
      label: t('invoice.summary.vat'),
      value: totals.totalVAT.toFixed(decimalPlaces),
    },
  ].map(({ label, value }) => (
    <div
      key={label}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : 0,
        padding: '12px 18px',
        borderBottom: '1px solid #f1f5f9',
      }}
    >
      <span
        style={{
          fontSize: '14px',
          fontWeight: '700',
          color: '#64748b',
        }}
      >
        {label}
      </span>

      <span
        style={{
          fontSize: '15px',
          fontWeight: '800',
          color: '#1e293b',
        }}
      >
        {value}
      </span>
    </div>
  ))}

  {/* صافي الفاتورة */}
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '18px 20px',
      background: 'linear-gradient(to left, #1d4ed8, #2563eb)',
    }}
  >
    <span
      style={{
        color: 'white',
        fontSize: '16px',
        fontWeight: '700',
      }}
    >
      {t('invoice.summary.net')}
    </span>

    <span
      style={{
        color: 'white',
        fontSize: isMobile ? '22px' : '28px',
        fontWeight: '900',
      }}
    >
      {totals.net.toFixed(decimalPlaces)}
    </span>
  </div>
</div>


        </div>

      </form>
    </div>

    {/* ── BULK INSERT ── */}
    {bulkOpen && !viewMode && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={() => setBulkOpen(false)} />
        <div style={{ position: 'relative', background: 'white', width: '100%', maxWidth: '760px', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
            <h3 style={{ margin: 0, fontWeight: '800', fontSize: '16px', color: '#1e293b' }}>📦 إدراج متعدد للأصناف</h3>
            <button onClick={() => setBulkOpen(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#94a3b8', fontWeight: '700' }}>✕</button>
          </div>
          <div style={{ padding: '16px' }}>
            <input autoFocus placeholder="بحث بالاسم أو جزء منه، الرمز، أو الباركود..." value={bulkSearch} onChange={(e) => setBulkSearch(e.target.value)}
              onKeyDown={(e) => {
                // Enter على باركود مطابق: يحدد الصنف مباشرة
                if (e.key !== "Enter") return;
                e.preventDefault();
                const first = bulkResults.rows[0];
                if (first && bulkResults.total === 1) { toggleBulkProduct(first.id, true); setBulkSearch(""); }
              }}
              style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '600', background: '#f8fafc', outline: 'none', marginBottom: '8px', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px', fontSize: '12.5px', color: '#64748b', flexWrap: 'wrap' }}>
              <span>
                {bulkResults.total.toLocaleString('en-US')} صنف
                {bulkResults.total > BULK_LIMIT && ` — يظهر أول ${BULK_LIMIT}، اكتب أكثر لتضييق البحث`}
                {bulkBalancesLoading && ' · جاري تحميل الأرصدة...'}
                <span style={{ color: '#94a3b8' }}> · نقرة للتحديد، نقر مزدوج للإدراج</span>
              </span>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontWeight: 700 }}>
                <input type="checkbox" checked={bulkOnlySelected} onChange={(e) => setBulkOnlySelected(e.target.checked)} />
                المحددة فقط ({Object.keys(bulkSelected).length})
              </label>
            </div>
            <div style={{ maxHeight: '340px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', fontSize: '12px', color: '#64748b', width: '44px' }}>✓</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700', fontSize: '12px', color: '#64748b' }}>الصنف</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', fontSize: '12px', color: '#64748b', width: '90px' }}>الوحدة</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', fontSize: '12px', color: '#64748b', width: '95px' }}>الكمية الحالية</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', fontSize: '12px', color: usesPurchasePrice ? '#047857' : '#4338ca', width: '110px' }}>
                      {usesPurchasePrice ? 'سعر الشراء' : 'سعر البيع'}
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', fontSize: '12px', color: '#64748b', width: '90px' }}>الكمية</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.rows.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: '28px', textAlign: 'center', color: '#94a3b8' }}>لا توجد أصناف مطابقة</td></tr>
                  )}
                  {bulkResults.rows.map(p => {
                    const unitsList = Array.isArray(p.units) ? p.units : [];
                    const bu = unitsList.find(u => u.isBaseUnit) || {};
                    const qtyVal = bulkSelected[p.id] ?? 0;
                    const checked = Object.prototype.hasOwnProperty.call(bulkSelected, p.id);
                    return (
                      <tr key={p.id}
                        // نقرة في أي مكان بالسطر تحدد/تلغي التحديد
                        onClick={() => toggleBulkProduct(p.id, !checked)}
                        // نقر مزدوج: يدرج المحدد + هذا الصنف (النقرتان قبله أعادتا حالته كما كانت)
                        onDoubleClick={() => handleBulkInsert({ ...bulkSelected, [p.id]: bulkSelected[p.id] ?? 1 })}
                        title="نقرة: تحديد — نقر مزدوج: إدراج المحدد"
                        style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', userSelect: 'none',
                          background: checked ? '#eef2ff' : undefined, transition: 'background .12s' }}>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}
                          onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={!!checked} onChange={(e) => toggleBulkProduct(p.id, e.target.checked)} />
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ fontWeight: '700', color: '#1e293b' }}>{p.arabicName}</div>
                          {(p.code || p.barcode) && (
                            <div style={{ fontSize: '11.5px', color: '#94a3b8', fontFamily: 'monospace', direction: 'ltr', textAlign: 'right' }}>
                              {[p.code, p.barcode].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>{bu?.unitNameAr || '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '800', fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                          color: p.isService ? '#94a3b8' : (bulkBalances[p.id] ?? 0) <= 0 ? '#dc2626' : '#0f172a' }}>
                          {p.isService ? '—' : (Math.round((bulkBalances[p.id] ?? 0) * 1000) / 1000).toLocaleString('en-US')}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '800', fontVariantNumeric: 'tabular-nums', color: usesPurchasePrice ? '#047857' : '#4338ca' }}>
                          {pickUnitPriceForDirection(bu, p)}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}
                          onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                          <input type="number" min="0" step="1" value={qtyVal || 1}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setBulkProductQty(p.id, e.target.value)}
                            style={{ width: '70px', height: '34px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', textAlign: 'center', background: 'white', outline: 'none' }} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
<div
  style={{
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '14px 22px',
    borderTop: '1px solid #e2e8f0',
  }}
>
  <Button
    type="button"
    variant="outlined"
    color="secondary"
    onClick={() => setBulkOpen(false)}
  >
    إلغاء
  </Button>

  <Button
    type="button"
    variant="contained"
    color="primary"
    onClick={() => handleBulkInsert()}
  >
    إدراج الأصناف المحددة
  </Button>
</div>

        </div>
      </div>
    )}

    <Dialog open={openCustomerModal} onClose={() => setOpenCustomerModal(false)} dir="rtl" maxWidth="sm" fullWidth>
      <DialogTitle>إضافة عميل جديد</DialogTitle>
      <DialogContent>
        <CustomerForm isModal={true} onSuccess={handleCustomerSuccess} onCancel={() => setOpenCustomerModal(false)} />
      </DialogContent>
    </Dialog>

    <Dialog open={openSupplierModal} onClose={() => setOpenSupplierModal(false)} dir="rtl" maxWidth="sm" fullWidth>
      <DialogTitle>إضافة مورد جديد</DialogTitle>
      <DialogContent>
        <SupplierForm isModal={true} onSuccess={handleSupplierSuccess} onCancel={() => setOpenSupplierModal(false)} />
      </DialogContent>
    </Dialog>

    <InvoiceLayoutDialog
      open={layoutOpen}
      typeName={types.find(x => Number(x.invoiceTypeId) === Number(form.invoiceTypeId))?.name}
      headerFields={visibleFields}
      itemFields={visibleItemFields}
  itemOrder={itemOrder}
  itemWidths={itemWidths}
      headerLabels={headerLabels}
      itemLabels={itemLabels}
      onClose={() => setLayoutOpen(false)}
      onSave={handleSaveLayout}
    />
    <ImportInvoiceItemsDialog open={importOpen} onClose={() => setImportOpen(false)} onImport={handleImportSuccess} products={products} />

  </div>
);
}